/* Wix indexing decoder. Sorts the URLs a store owner copies from Search
   Console's Page indexing report by the kind of Wix page they are, and says,
   for each reason and page type, whether that is normal on Wix, worth a look,
   or a fix, with the steps and the click paths.

   Everything here is a plain function (no DOM, no network, no storage), so
   the server-rendered first paint (wix-indexing-decoder.cjs), the browser and
   the tests run the same code. Wix and Google statements come from the
   verified research sheets: R4 (indexing and Search Console), R2 (redirects),
   R7 (hidden, deleted and sold-out products) and R1a (SEO settings). Anything
   the sheets mark unverified is left out or worded as "check this yourself". */
(function (root, factory) {
  'use strict';
  const shared = typeof require === 'function' && typeof module === 'object' && module.exports
    ? require('./wix-shared.js')
    : root.WixShared;
  const api = factory(shared || null);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.WixIndexing = api;
  if (typeof document !== 'undefined' && typeof document.querySelector === 'function') {
    const start = () => api.init(document.querySelector('[data-wid-lab]'));
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function (shared) {
  'use strict';

  const META = Object.freeze({
    path: '/tools/wix-indexing-decoder',
    name: 'Wix indexing decoder',
    summary: 'Sort a Search Console “not indexed” list by Wix page type and see what to do.'
  });

  // Search Console lists at most 1,000 examples per reason, so 5,000 covers
  // several exports pasted together. More is cut, with a note, never frozen.
  const MAX_URLS = 5000;
  const MAX_CHARS = 1500000;
  const MAX_FILE_BYTES = 5 * 1024 * 1024;
  const EXAMPLES = 5;
  const PAGE_SIZE = 100;

  const esc = shared && shared.esc
    ? shared.esc
    : value => String(value).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const tidy = shared && shared.tidyText
    ? shared.tidyText
    : value => String(value == null ? '' : value).replace(/[\u200b-\u200d\u2060\ufeff]/g, '').replace(/\s+/g, ' ').trim();
  const num = n => n.toLocaleString('en-US');
  const plural = (count, one, many = `${one}s`) => `${num(count)} ${count === 1 ? one : many}`;
  // "Both are …" for two, "All 1,250 are …" for more.
  const allOf = n => (n === 2 ? 'Both' : `All ${num(n)}`);
  const joinWords = words => (words.length < 2 ? words.join('') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`);
  const decodeSafe = value => { try { return decodeURIComponent(value); } catch (_) { return value; } };

  // ---- Sources (each fact in the copy points at one of these) -------------

  const WIX = 'https://support.wix.com/en/article/';
  const SOURCES = Object.freeze({
    G1: ['Google Search Console Help: Page indexing report', 'https://support.google.com/webmasters/answer/7440203'],
    G2: ['Google Search Console Help: URL Inspection tool', 'https://support.google.com/webmasters/answer/9012289'],
    G3: ['Google Search Central: Ask Google to recrawl your URLs', 'https://developers.google.com/search/docs/crawling-indexing/ask-google-to-recrawl'],
    G4: ['Google Search Central: The site: search operator', 'https://developers.google.com/search/docs/monitor-debug/search-operators/all-search-site'],
    G5: ['Google Search Central: How Google Search works', 'https://developers.google.com/search/docs/fundamentals/how-search-works'],
    G9: ['Google Search Console Help: Sitemaps report', 'https://support.google.com/webmasters/answer/7451001'],
    G10: ['Google Search Central: What is canonicalization', 'https://developers.google.com/search/docs/crawling-indexing/canonicalization'],
    G11: ['Google Search Central: Troubleshooting canonicalization', 'https://developers.google.com/search/docs/crawling-indexing/canonicalization-troubleshooting'],
    G13: ['Google Search Central: Office hours, July 2023', 'https://developers.google.com/search/help/office-hours/2023/july'],
    G14: ['Google Search Central Blog: Index coverage data improvements (2021)', 'https://developers.google.com/search/blog/2021/01/index-coverage-data-improvements'],
    G15: ['Google Search Central: Creating helpful content', 'https://developers.google.com/search/docs/fundamentals/creating-helpful-content'],
    GSTATUS: ['Google crawling docs: HTTP status codes and soft 404s', 'https://developers.google.com/crawling/docs/troubleshooting/http-status-codes'],
    GMOVE: ['Google Search Central: Site moves with URL changes', 'https://developers.google.com/search/docs/crawling-indexing/site-move-with-url-changes'],
    GECOM: ['Google Search Central: URL structure for ecommerce sites', 'https://developers.google.com/search/docs/specialty/ecommerce/designing-a-url-structure-for-ecommerce-sites'],
    GPAUSE: ['Google Search Central: Pausing an online business', 'https://developers.google.com/search/docs/crawling-indexing/pause-online-business'],
    W1: ['Wix Help: Site or pages not appearing in search results', `${WIX}site-or-pages-not-appearing-in-search-engine-results`],
    W2: ['Wix Help: Letting search engines index your site', `${WIX}enabling-search-engines-like-google-to-index-your-site`],
    W3: ['Wix Help: Connecting your site to Google', `${WIX}connecting-your-site-to-google-in-the-wix-seo-wiz`],
    W4: ['Wix Help: Verifying your site with Google Search Console', `${WIX}verifying-your-site-with-google-search-console-5033765`],
    W7: ['Wix Help: Understanding your site’s sitemap file', `${WIX}understanding-your-sites-sitemap-file`],
    W8: ['Wix Help: Preventing search engines from indexing a page', `${WIX}wix-editor-preventing-search-engines-from-indexing-your-page`],
    W9: ['Wix Help: Customizing your SEO settings', `${WIX}customizing-your-seo-settings-1420929`],
    W10: ['Wix Help: Your site’s default SEO settings', `${WIX}understanding-your-sites-default-seo-settings-6447365`],
    W14: ['Wix Help: Editing your site’s robots.txt file', `${WIX}editing-your-sites-robotstxt-file`],
    W15: ['Wix Help: Using the robots meta tag', `${WIX}using-the-robots-meta-tag`],
    W16: ['Wix Help: Changing the canonical tags for your pages', `${WIX}changing-the-canonical-tags-for-your-sites-pages`],
    W17: ['Wix Help: Multiple domains on your site and SEO', `${WIX}multiple-domains-on-your-site-and-seo`],
    W18: ['Wix Help: Your free Wix URL after connecting a domain', `${WIX}request-removing-your-free-wix-url-after-connecting-your-domain`],
    W21: ['Wix Help: The Wix SEO Assistant', `${WIX}improving-your-pages-seo-geo-with-the-wix-seo-assistant`],
    W23: ['Wix Help: SEO for Wix Stores category pages', `${WIX}wix-stores-seo-for-category-pages`],
    W24: ['Wix Help: About SSL and HTTPS', `${WIX}about-ssl-and-https`],
    W26: ['Wix Help: SEO in the Wix Harmony editor', `${WIX}wix-harmony-editor-optimizing-your-site-with-seo`],
    W27: ['Wix Help: Keeping the free Wix URL', `${WIX}keeping-the-free-wix-url-4956247`],
    W31: ['Wix Help: Switching your primary and redirected domains', `${WIX}switching-your-primary-and-redirected-domains`],
    WSI: ['Wix Help: Wix Site Inspection', `${WIX}wix-site-inspection-an-overview`],
    W12: ['Wix Help: Understanding your Site Inspection report', `${WIX}wix-site-inspection-understanding-your-site-inspection-report`],
    WMULTI: ['Wix Help: Verifying multilingual subdomains with Google Search Console', `${WIX}wix-multilingual-verifying-your-multilingual-subdomains-and-submitting-sitemaps-with-google-search-console`],
    WCATCREATE: ['Wix Help: Creating product categories', `${WIX}wix-stores-creating-product-categories`],
    WSEOBP: ['Wix Help: SEO best practices for ecommerce and product pages', `${WIX}seo-best-practices-for-ecommerce-and-product-pages`],
    WPRODUCT: ['Wix Help: SEO settings for product pages', `${WIX}wix-stores-optimizing-your-seo-for-product-pages`],
    WURL: ['Wix Help: Changing your page URL', `${WIX}wix-editor-changing-your-page-url`],
    WSTRUCT: ['Wix Help: Customizing your URL structure in SEO Settings', `${WIX}customizing-your-pages-url-structure-in-seo-settings`],
    WREDIRECT: ['Wix Help: Setting up a 301 redirect', `${WIX}setting-up-a-301-redirect-for-a-page-on-your-site`],
    WLOOP: ['Wix Help: Checking your 301 redirects', `${WIX}checking-that-your-301-redirects-are-set-up-correctly`],
    WVIS: ['Wix Help: Managing product visibility', `${WIX}wix-stores-managing-product-visibility`],
    WCAT: ['Wix Help: About product categories', `${WIX}wix-stores-about-product-categories-8914224`],
    E_REDIRECT: ['Wix Help: Search Console “Page with redirect”', `${WIX}google-search-console-excluded-page-with-redirect`],
    E_ANOMALY: ['Wix Help: Search Console “Crawl anomaly”', `${WIX}google-search-console-excluded-crawl-anomaly`],
    E_DISC: ['Wix Help: Search Console “Discovered - currently not indexed”', `${WIX}google-search-console-discovered-currently-not-indexed`],
    E_CRAWLED: ['Wix Help: Search Console “Crawled - currently not indexed”', `${WIX}google-search-console-crawled-currently-not-indexed`],
    E_NOINDEX: ['Wix Help: Search Console “Submitted URL marked noindex”', `${WIX}google-search-console-submitted-url-marked-noindex`],
    E_SOFT: ['Wix Help: Search Console soft 404 errors', `${WIX}google-search-console-soft-404-error`],
    E_404: ['Wix Help: Search Console 404 errors', `${WIX}google-search-console-404-error`],
    E_403: ['Wix Help: Search Console 403 errors', `${WIX}google-search-console-403-error`],
    E_5XX: ['Wix Help: Search Console 5xx errors', `${WIX}google-search-console-5xx-error`],
    H1: ['Wix SEO Hub: What is Google Search Console', 'https://www.wix.com/seo/learn/resource/what-is-google-search-console']
  });

  // ---- Page types ----------------------------------------------------------

  // Wix's default URL prefixes (Wix Help: default SEO settings). Product and
  // blog post prefixes can be changed in SEO Settings, so visitors can add
  // their own (prefixTable). Each needs at least one part after the prefix.
  const PATH_TYPES = Object.freeze([
    { type: 'product', segs: ['product-page'] },
    { type: 'category', segs: ['category'] },
    { type: 'post', segs: ['post'] },
    // Older Wix blogs still use /single-post/ beside /post/.
    { type: 'post', segs: ['single-post'] },
    { type: 'blogCategory', segs: ['blog', 'categories'] },
    { type: 'blogTag', segs: ['blog', 'tags'] },
    { type: 'blogArchive', segs: ['blog', 'archive'] },
    { type: 'service', segs: ['service-page'] },
    { type: 'event', segs: ['event-details'] },
    { type: 'member', segs: ['members-area'] },
    { type: 'program', segs: ['online-program-page'] }
  ]);
  // Cart, checkout, thank-you and log-in pages, matched by the plain word in
  // the first part of the path. Wix doesn't document how it handles them
  // for search, so their verdict stays neutral.
  const SYSTEM_WORDS = new Set(['cart', 'cart-page', 'checkout', 'thank-you', 'thank-you-page', 'thankyou', 'order-confirmation', 'login', 'log-in', 'signin', 'sign-in', 'signup', 'sign-up', 'register', 'account', 'my-account', 'search', 'search-results', 'wishlist']);
  const FILE_TYPES = new Set(['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'avif', 'bmp', 'ico', 'mp4', 'mov', 'webm', 'mp3', 'wav', 'zip', 'txt', 'xml', 'csv', 'json', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'css', 'js', 'woff', 'woff2']);
  // Bare names like "robots.txt" or "price-list.pdf" are files, not websites.
  const FILE_LIKE = new Set([...FILE_TYPES, 'html', 'htm', 'php', 'asp', 'aspx']);
  // ISO 639-1 language codes. Wix Multilingual puts each language in a folder
  // by default (/fr/…), which doesn't change what kind of page it is.
  const LANGS = new Set('aa ab ae af ak am an ar as av ay az ba be bg bh bi bm bn bo br bs ca ce ch co cr cs cu cv cy da de dv dz ee el en eo es et eu fa ff fi fj fo fr fy ga gd gl gn gu gv ha he hi ho hr ht hu hy hz ia id ie ig ii ik io is it iu ja jv ka kg ki kj kk kl km kn ko kr ks ku kv kw ky la lb lg li ln lo lt lu lv mg mh mi mk ml mn mr ms mt my na nb nd ne ng nl nn no nr nv ny oc oj om or os pa pi pl ps pt qu rm rn ro ru rw sa sc sd se sg si sk sl sm sn so sq sr ss st su sv sw ta te tg th ti tk tl tn to tr ts tt tw ty ug uk ur uz ve vi vo wa wo xh yi yo za zh zu'.split(' '));
  const isLang = part => { const match = /^([a-z]{2})(?:-[a-z]{2,4})?$/.exec(part || ''); return Boolean(match && LANGS.has(match[1])); };

  const TYPES = Object.freeze({
    product: { label: 'Product pages', one: 'product page', many: 'product pages', a: 'a product page', hint: '/product-page/…', sitemap: '/store-products-sitemap.xml' },
    category: { label: 'Store category pages', one: 'category page', many: 'category pages', a: 'a category page', hint: '/category/…' },
    post: { label: 'Blog posts', one: 'blog post', many: 'blog posts', a: 'a blog post', hint: '/post/…', sitemap: '/blog-posts-sitemap.xml' },
    blogCategory: { label: 'Blog category pages', one: 'blog category page', many: 'blog category pages', a: 'a blog category page', hint: '/blog/categories/…' },
    blogTag: { label: 'Blog tag pages', one: 'blog tag page', many: 'blog tag pages', a: 'a blog tag page', hint: '/blog/tags/…' },
    blogArchive: { label: 'Blog archive pages', one: 'blog archive page', many: 'blog archive pages', a: 'a blog archive page', hint: '/blog/archive/…' },
    service: { label: 'Booking service pages', one: 'service page', many: 'service pages', a: 'a service page', hint: '/service-page/…' },
    event: { label: 'Event pages', one: 'event page', many: 'event pages', a: 'an event page', hint: '/event-details/…' },
    member: { label: 'Member pages', one: 'member page', many: 'member pages', a: 'a member page', hint: '/members-area/…' },
    program: { label: 'Online program pages', one: 'program page', many: 'program pages', a: 'a program page', hint: '/online-program-page/…' },
    home: { label: 'Home page', one: 'home page', many: 'home page URLs', a: 'your home page', hint: 'nothing after the domain, or a language folder like /fr' },
    page: { label: 'Site pages', one: 'site page', many: 'site pages', a: 'a site page', hint: 'one-part URLs, like /about', sitemap: '/pages-sitemap.xml' },
    system: { label: 'System pages', one: 'system page', many: 'system pages', a: 'a system page', hint: 'cart, checkout, thank-you, log in' },
    params: { label: 'URLs with ? parameters', one: 'URL with a ? parameter', many: 'URLs with ? parameters', a: 'a URL with a ? parameter', hint: 'extra bits after a ?, like ?fb=1' },
    http: { label: 'http:// URLs', one: 'http:// URL', many: 'http:// URLs', a: 'an http:// URL', hint: 'http:// instead of https://' },
    hostVariant: { label: 'URLs without www', one: 'URL without www', many: 'URLs without www', a: 'a URL without www', hint: 'the other form of your address' },
    freeWix: { label: 'Your free Wix URL', one: 'free Wix URL', many: 'free Wix URLs', a: 'your free Wix URL', hint: '…wixsite.com, …wixstudio.com or …wixstudio.io' },
    subdomain: { label: 'Other subdomains', one: 'URL on another subdomain', many: 'URLs on other subdomains', a: 'a URL on another subdomain', hint: 'like shop.yourstore.com' },
    otherHost: { label: 'Other websites', one: 'URL on another website', many: 'URLs on other websites', a: 'a URL on another website', hint: 'not your site address' },
    file: { label: 'Files', one: 'file', many: 'files', a: 'a file', hint: 'PDFs, images, sitemaps' },
    wixApi: { label: 'Wix system URLs', one: 'Wix system URL', many: 'Wix system URLs', a: 'a Wix system URL', hint: '/_api/…' },
    other: { label: 'Other URLs', one: 'other URL', many: 'other URLs', a: 'another URL', hint: 'don’t match Wix’s default prefixes' }
  });
  // A stable order for ties, the filter list and the status table.
  const TYPE_ORDER = Object.freeze(['product', 'category', 'post', 'page', 'home', 'params', 'system', 'blogCategory', 'blogTag', 'blogArchive', 'member', 'service', 'event', 'program', 'http', 'hostVariant', 'freeWix', 'subdomain', 'otherHost', 'file', 'wixApi', 'other']);

  // ---- Verdicts --------------------------------------------------------------

  const LEVEL_RANK = Object.freeze({ fix: 0, look: 1, normal: 2 });
  const LEVEL_WORD = Object.freeze({ fix: 'Fix', look: 'Take a look', normal: 'Normal' });
  const V = (level, text, steps = []) => Object.freeze({ level, text, steps: Object.freeze(steps) });

  // Steps name the Wix place to click and the source for it. Tokens in
  // braces ({these} {pages} …) fit the sentence to one URL or many.
  const STEPS = Object.freeze({
    inspect: { text: 'Paste one URL into <b>URL Inspection</b> in Search Console to see Google’s own status for it.', sources: ['G2'] },
    'inspect-canonical': { text: 'In <b>URL Inspection</b>, compare <b>User-declared canonical</b> with <b>Google-selected canonical</b>. Read the indexed result: Google says the live test doesn’t check duplicates.', sources: ['G1', 'G2'] },
    'no-resubmit': { text: 'Don’t keep resubmitting. Google says there’s “no need to resubmit”, and asking again for the same URL “won’t get it crawled any faster.” Ask once, after an important change.', sources: ['G1', 'G3'] },
    'own-words': { text: 'Give each {page} words of its own: what it is, what it’s made of, who it’s for. Wix’s advice: “Avoid using descriptions from manufacturers to prevent duplicate content issues.”', sources: ['WSEOBP', 'G5'] },
    'merge-posts': { text: 'Merge short posts that cover the same thing, and make sure each post says something the others don’t.', sources: ['G5'] },
    'internal-links': { text: 'Link to {these} {pages} from pages Google already knows, like your menu, category pages or related posts. Google finds pages through links and your sitemap.', sources: ['G1', 'W1'] },
    sitemap: { text: 'Check {they} {are} in your sitemap. Wix makes it for you at /sitemap.xml and lists {many} in {sitemap}.', sources: ['W7'] },
    wait: { text: 'Give it time. Google says crawling “can take anywhere from a few days to a few weeks.”', sources: ['G3'] },
    'category-copy': { text: 'Add a description to each category in <b>Categories</b>, so the page says more than its product grid. Wix’s sitemap help lists no sitemap just for store categories, so link each one from your menu too.', sources: ['W23', 'W7'] },
    'category-empty': { text: 'Empty category? Google suggests keeping categories with no products out of its index. Turn off <b>Active on your site</b>, or the category’s <b>Let search engines index this page</b>.', sources: ['GECOM', 'WCAT', 'W23'] },
    'check-original': { text: 'Check the original URL instead, the part before the ?. Wix points copies like these back to it.', sources: ['W16'] },
    'check-https': { text: 'Check the https:// version of the URL instead. All Wix sites use https://.', sources: ['W24'] },
    'check-main-host': { text: 'Check the same page on your main address, {main}.', sources: ['E_REDIRECT'] },
    'landing-check': { text: 'Open {few} in your browser and check {each} lands on the right page.', sources: ['E_REDIRECT'] },
    'redirect-manager': { text: 'Lands on the wrong page? Change the redirect in <b>SEO & GEO</b> › <b>Tools and settings</b> › <b>URL Redirect Manager</b>.', sources: ['WREDIRECT'] },
    'primary-domain': { text: 'Check which domain is set as primary under <b>Domains</b> in your Wix account. Wix says only the primary domain appears in search results.', sources: ['W17', 'W31'] },
    'leave-404': { text: 'Gone for good, with nothing like it on your site? Leave the 404. Wix says the error clears from Search Console on its own.', sources: ['G1', 'E_404'] },
    'redirect-replacement': { text: 'Has a close replacement? Add a 301 redirect to it: <b>SEO & GEO</b> › <b>Tools and settings</b> › <b>URL Redirect Manager</b> › <b>+ New Redirect</b> › <b>Single redirect</b>. Redirects need a connected domain. Many to do? <a href="/tools/wix-redirect-builder">Build them as a CSV</a>.', sources: ['WREDIRECT'] },
    'no-home-redirect': { text: 'Don’t point them all at your home page. Google says redirecting many old URLs to one unrelated page “might be treated as a soft 404 error.”', sources: ['GMOVE'] },
    'hidden-product': { text: 'Hid a product only for a while? Wix says a hidden product’s URL returns a 404. First set it to out of stock, then show it again, so the page comes back but can’t be bought. Google says to “still allow that page, and mark it out of stock.”', sources: ['WVIS', 'GPAUSE'] },
    'category-active': { text: 'Switched a category off? Wix says an inactive category “no longer has a URL” and is hidden from search engines. Turn <b>Active on your site</b> back on if you still want it.', sources: ['WCATCREATE', 'WCAT'] },
    'category-redirect': { text: 'Renamed a category’s URL slug? Wix’s help pages disagree on whether Wix adds a redirect. Check the <b>URL Redirect Manager</b> and add one if it’s missing.', sources: ['W23', 'WREDIRECT'] },
    'blog-redirect': { text: 'Moved a post? Wix doesn’t add a redirect when you change a post’s URL slug, so add one in the <b>URL Redirect Manager</b>.', sources: ['WREDIRECT'] },
    'noindex-product': { text: 'Product: <b>Products</b> › the product › <b>Edit SEO Settings</b> (under Marketing & SEO) › <b>Basics</b> › turn on <b>Let search engines index this page</b>. With this switch off, Wix also hides the product from your site’s own search.', sources: ['WPRODUCT'] },
    'noindex-category': { text: 'Category: <b>Categories</b> › the category › <b>Edit SEO Settings</b> › turn on <b>Let search engines index this page</b>. No SEO settings there? Wix says that’s the older category page.', sources: ['W23'] },
    'noindex-post': { text: 'Blog post: <b>SEO & GEO</b> › <b>Tools and settings</b> › <b>SEO Settings</b> › blog posts › <b>Edit by Post</b> › the post › <b>Basics</b> › <b>Let search engines index this page</b>.', sources: ['W9'] },
    'noindex-page': { text: 'Page: in the Wix Editor, <b>Pages & Menu</b> › <b>More Actions</b> › <b>SEO basics</b>; in Wix Studio, <b>Pages</b> › <b>More Actions</b> › <b>SEO Basics</b>. Turn on <b>Let search engines index this page</b>. In Wix Harmony, the <b>Page</b> drop-down › <b>More Actions</b> › <b>SEO and accessibility</b> › <b>SEO</b> › <b>Let search engines index this page</b> › <b>Edit</b>, then turn it on.', sources: ['W8', 'W26'] },
    'noindex-type': { text: 'Whole page type: <b>SEO & GEO</b> › <b>Tools and settings</b> › <b>SEO Settings</b> › the page type › <b>Customize defaults</b> › <b>Edit</b> next to Basics & social share › <b>Let search engines index your page(s)</b>.', sources: ['W9'] },
    'noindex-site': { text: 'Whole site: <b>SEO & GEO</b> › <b>Tools and settings</b> › <b>SEO Settings</b> › <b>Let search engines index your site</b>. Off here means off for every page.', sources: ['W2', 'W8'] },
    'robots-meta': { text: 'Still marked noindex? Check the page’s <b>Advanced SEO</b> tab › <b>Robots Meta Tag</b> for noindex.', sources: ['W15'] },
    'robots-meta-product': { text: 'Still marked noindex? In the product’s <b>Edit SEO Settings</b>, check the <b>Advanced</b> tab › <b>Robots Meta Tag</b> for noindex.', sources: ['W15'] },
    'seo-assistant': { text: 'In the product’s <b>Edit SEO Settings</b>, check Wix’s SEO Assistant. Until the page can be indexed, it shows a Critical task: “Fix what’s needed to let search engines index this page”.', sources: ['W21'] },
    access: { text: 'Check {they} {arent} password-protected or for members only: search engines can’t index those pages.', sources: ['W1'] },
    'robots-no-edit': { text: 'Leave robots.txt alone: Wix says “you don’t need to edit your robots.txt file.” Fix the page’s own settings instead.', sources: ['W14'] },
    retest: { text: 'Open {these} {pages} in your browser. If {they} load{s}, run a live test in <b>URL Inspection</b>.', sources: ['E_5XX', 'G2'] },
    validate: { text: 'Once {they} load{s}, click <b>Validate fix</b> in the report. Validation typically takes “up to about two weeks”: don’t click it again until it has succeeded or failed. It stops if one page still has the error, so fix them all first.', sources: ['G1'] },
    'contact-wix': { text: 'Errors keep coming back? Contact Wix and send them the URLs.', sources: ['E_5XX'] },
    'follow-redirect': { text: 'Open {anyOne} in your browser and see where it ends up. Then point the redirect straight at that final page in the <b>URL Redirect Manager</b>.', sources: ['GMOVE', 'WREDIRECT'] },
    'chain-tool': { text: 'Many redirects? Check them for chains and loops with the <a href="/tools/wix-redirect-builder">Wix redirect builder</a>.', sources: [] },
    'removals-page': { text: 'Open <b>Removals</b> in Search Console and check the request there.', sources: ['G2'] },
    'out-of-stock': { text: 'For a sold-out product, keep the page and mark it out of stock. Google says it’s “better to still allow that page, and mark it out of stock.”', sources: ['GPAUSE'] },
    'live-test': { text: 'Run a live test in <b>URL Inspection</b> to see what Google gets from the page now.', sources: ['G2'] },
    'open-live': { text: 'Open each one that should be live. If a page looks empty, add real content; if it’s gone for good, a plain 404 is fine.', sources: ['GSTATUS'] },
    'home-try-request': { text: 'It’s your most important page. Wix says that for an important page whose live test shows no errors, you can try <b>Request indexing</b>, once.', sources: ['E_CRAWLED'] },
    'home-request': { text: 'Use <b>Request indexing</b> on your home page, once. Google calls that “the best first step” for a new site.', sources: ['G1'] },
    'home-content': { text: 'Check your home page shows text about your store that Google can read, not only pictures.', sources: ['G5'] },
    'merge-duplicates': { text: 'Same product twice? Wix’s advice: delete one of the duplicate pages, or add a canonical tag.', sources: ['H1'] },
    differ: { text: 'Two different {many} that read alike? Make them clearly different: name, description, photos. Google’s fix is to make sure the content “differs substantially”.', sources: ['G1'] },
    'canonical-tag': { text: 'To pick the main version yourself, use the page’s <b>Advanced SEO</b> tab › <b>Additional tags</b> › canonical. Wix warns a changed canonical may drop the page from your sitemap.', sources: ['W16', 'W7'] },
    'subdomain-verify': { text: 'A language version of your site? Wix’s help has you verify its subdomain in Search Console on its own, as a URL-prefix property.', sources: ['WMULTI'] },
    'check-site': { text: 'Check the site address you typed, and that you copied the list from the same site in Search Console.', sources: [] },
    'custom-prefix': { text: 'Changed your product or blog URL prefix? Add it under <b>Your URL prefixes</b> so these sort properly.', sources: ['WSTRUCT'] }
  });

  // A page-type-aware step: the index switch lives in a different place for
  // products, categories, posts, the home page and other pages.
  function stepIdFor(id, type) {
    if (id === 'robots-meta' && type === 'product') return 'robots-meta-product';
    if (id !== 'index-switch') return id;
    return { product: 'noindex-product', category: 'noindex-category', post: 'noindex-post', home: 'noindex-site' }[type] || 'noindex-page';
  }

  // Verdicts that hold for a page type whatever the reason, unless a reason
  // says otherwise. These are the address and system groups.
  const TYPE_VERDICTS = Object.freeze({
    params: V('normal', '{These} {are} {copyOf} or {paged}, with extra bits after a ?, like ?fb=1 or ?page=2. Wix points {them} back to the main URL, so check that one instead.', ['check-original']),
    http: V('normal', 'All Wix sites use https://, and Wix sends http:// URLs on to the https:// version. Check the https:// URL instead.', ['check-https']),
    hostVariant: V('normal', '{These} use{s} {host} instead of your main address, {main}. Your site has one main address, so check the same page there.', ['check-main-host']),
    freeWix: V('normal', '{These} {are} on your free Wix URL, {host}. Once a domain is connected, Wix sends the free URL on to it, and Wix says search engines can no longer find it.', []),
    subdomain: V('look', '{These} {are} on {host}, another subdomain of your domain. It may be a language version of your site. If you don’t recognise it, find out what it is.', ['subdomain-verify', 'inspect']),
    otherHost: V('look', '{These} {are} on {host}, not on your site, {main}. If it’s another domain connected to this site, Wix redirects it to your primary domain. Otherwise, check you copied the list from the right site in Search Console.', ['check-site']),
    file: V('normal', 'Files like PDFs, images and sitemaps aren’t pages of your store. You can leave them, unless a file is something people search for.', []),
    wixApi: V('normal', '{These} start{s} with /_api/: a system address, not a page of your store. Wix’s help names one of these, /_api/common-services/notification/invoke, and says to ignore it.', []),
    system: V('normal', 'Usually pages you don’t need in Google. Wix doesn’t say how it handles them.', []),
    blogTag: V('normal', 'Wix keeps blog tag pages out of Google by default. They list posts that have pages of their own. Nothing to do.', []),
    blogArchive: V('normal', 'Wix keeps blog archive pages out of Google by default. They list posts that have pages of their own. Nothing to do.', []),
    member: V('normal', 'Wix’s default settings page says member profile pages are noindex, though another Wix page says to set this yourself. Members-only pages can’t be indexed. Nothing to do, unless you want public profiles in Google.', [])
  });
  // On the two "indexed, with a warning" reasons the pages ARE in Google, so
  // "Wix keeps these out by default" doesn't apply to tags, archives or members.
  const INDEXED_GLOBALS = new Set(['params', 'http', 'hostVariant', 'freeWix', 'subdomain', 'otherHost', 'file', 'wixApi', 'system']);

  const REASONS = [
    {
      id: 'crawled',
      // Page types the status table names as normal for this reason (tests
      // check each one against the verdict rules below).
      normalFor: ['params', 'system'],
      label: 'Crawled - currently not indexed',
      aliases: [],
      group: 'not',
      meaning: 'Google visited these pages but chose not to add them, for now.',
      sources: ['G1', 'G5', 'G15', 'E_CRAWLED'],
      means: [
        'Google read these pages and left them out of its index, for now. Google says a page like this “may or may not be indexed in the future” and that there’s “no need to resubmit” it. It doesn’t say why.',
        'Google names low-quality content as a common reason a page isn’t indexed. On a store, start with product pages that have little text of their own, or text that repeats other pages.',
        'Wix’s help agrees resubmitting isn’t necessary, though it says you can try Request indexing. Wix also suggests at least 250–400 words on a page; Google says it has no preferred word count. Aim for useful, not long.'
      ],
      dont: ['Don’t click Request indexing for every URL in the list. There’s a daily limit, and Google says a request “does not guarantee that the page will appear”.'],
      verdicts: {
        content: V('look', 'Google read {these} {pages} and left {them} out, for now. Google doesn’t say why, but names low-quality content as a common reason. Check that each page offers something of its own.', ['inspect', 'internal-links', 'no-resubmit']),
        product: V('look', 'Google read {these} {pages} and left {them} out, for now. It doesn’t say why. Check that each product page has words of its own, not text shared with other products.', ['inspect', 'own-words', 'internal-links', 'sitemap', 'no-resubmit']),
        category: V('look', 'Google read {these} {pages} and left {them} out, for now. A category page that only lists products says little the product pages don’t, so give each one a description of its own.', ['inspect', 'category-copy', 'category-empty', 'internal-links']),
        post: V('look', 'Google read {these} {pages} and left {them} out, for now. Google names low-quality content as a common reason, so look for short posts, or posts that cover the same thing.', ['inspect', 'merge-posts', 'internal-links', 'no-resubmit']),
        blogCategory: V('look', 'Lower priority: blog category pages list posts that have pages of their own. Check the posts first.', ['internal-links']),
        home: V('fix', 'Google read your home page and left it out. It’s the page that matters most, so start here.', ['inspect', 'home-content', 'home-try-request'])
      }
    },
    {
      id: 'discovered',
      normalFor: ['params', 'system'],
      label: 'Discovered - currently not indexed',
      aliases: [],
      group: 'not',
      meaning: 'Google knows these pages exist but hasn’t visited them yet.',
      sources: ['G1', 'G3', 'G13', 'E_DISC', 'W7'],
      means: [
        'Google has found these URLs but hasn’t crawled them yet. Google says it usually holds off when a visit “was expected to overload the site”, and reschedules it.',
        'Google finds pages through links on pages it already knows and through your sitemap. Wix makes your sitemap and keeps it up to date.',
        'Wix’s help says pages like these may need better content. Asked about this status on a large site, Google’s team said that “it’s more about overall website quality.”'
      ],
      dont: ['Don’t ask Google to index each URL by hand. For many pages, Google points to your sitemap, and Wix keeps yours up to date.'],
      verdicts: {
        content: V('look', 'Google knows about {these} {pages} but hasn’t visited {them} yet. New pages can wait: Google says crawling can take “a few days to a few weeks”.', ['wait', 'internal-links', 'inspect']),
        product: V('look', 'Google knows about {these} {pages} but hasn’t visited {them} yet. New pages can wait: Google says crawling can take “a few days to a few weeks”.', ['wait', 'internal-links', 'sitemap', 'own-words']),
        category: V('look', 'Google knows about {these} {pages} but hasn’t visited {them} yet. Link each category from your menu, so Google can find it from pages it already knows.', ['wait', 'internal-links', 'category-copy']),
        post: V('look', 'Google knows about {these} {pages} but hasn’t visited {them} yet. New posts can wait: Google says crawling can take “a few days to a few weeks”.', ['wait', 'internal-links', 'sitemap', 'merge-posts']),
        blogCategory: V('look', 'Lower priority: blog category pages list posts that have pages of their own. Check the posts first.', ['internal-links']),
        home: V('fix', 'Google knows your home page but hasn’t visited it yet. Google calls asking for your home page to be indexed the best first step for a new site.', ['home-request', 'inspect'])
      }
    },
    {
      id: 'alternate',
      normalFor: ['params'],
      label: 'Alternate page with proper canonical tag',
      aliases: [],
      group: 'not',
      meaning: 'A copy that points Google to the main version, which is indexed.',
      sources: ['G1', 'W16'],
      means: [
        'These URLs point Google to a main version of the page, and Google has indexed that main version. Google: “there is nothing you need to do.”',
        'On Wix, URLs with extra bits after a ?, like ?fb=1, are copies of this kind. Wix gives every page a canonical tag that names the main URL, and points copies like these back to the original.'
      ],
      dont: ['Don’t try to get these copies indexed. Their main versions are already in Google.'],
      verdicts: {
        content: V('normal', '{These} {pages} point{s} Google to another URL as the main version, and that version is indexed. Google says there’s nothing to do. Expected this exact URL in Google? Inspect it to see where it points.', ['inspect-canonical']),
        home: V('normal', 'Your home page URL points Google to another URL as the main version, and that version is indexed. If that’s your home page under its main address, there’s nothing to do.', ['inspect-canonical'])
      }
    },
    {
      id: 'dupNoCanonical',
      normalFor: ['params', 'http', 'hostVariant'],
      label: 'Duplicate without user-selected canonical',
      aliases: [],
      group: 'not',
      meaning: 'Google sees a copy of another page here and shows the other one.',
      sources: ['G1', 'W16', 'W7', 'H1'],
      means: [
        'Google treats each of these as a copy of another page, and shows the other page in search. Google calls this “not an error” but “working as intended”.',
        'Wix adds a canonical tag to every page by default and points URLs with ? parameters back to the original. Act only when two pages that should both be in Google look the same to Google.',
        'For a real pair, Google’s fix is to make the content differ substantially, or to mark the canonical yourself. Wix lets you set it in a page’s Advanced SEO tab, but warns that a changed canonical may drop the page from your sitemap.'
      ],
      dont: ['Don’t change canonical tags in bulk to clear this list. Most entries are working as intended.'],
      verdicts: {
        content: V('look', 'Google treats {these} {pages} as {copies} of other pages and shows those instead. That’s working as intended, unless {these} should be in Google too.', ['inspect-canonical', 'merge-duplicates', 'differ', 'canonical-tag']),
        home: V('look', 'Google treats your home page URL as a copy of another URL. Inspect it: if Google picked your home page under another form of your address, that’s fine.', ['inspect-canonical'])
      }
    },
    {
      id: 'dupGoogleChose',
      normalFor: [],
      label: 'Duplicate, Google chose different canonical than user',
      aliases: [],
      group: 'not',
      meaning: 'Your page says it’s the main version, but Google picked another URL.',
      sources: ['G1', 'G2', 'G10', 'G11'],
      means: [
        'These pages say they are the main version, but Google thinks another URL “makes a better canonical”. Google treats a canonical tag as “a hint, not a rule.”',
        'Inspect one and compare “User-declared canonical” with “Google-selected canonical”. If Google’s pick is fine with you, leave it. If not, make the pages clearly different: Google says “a duplicate page must be similar to the canonical.”',
        'Google can hold pages in a duplicate group for up to two weeks, so recent changes may not show yet.'
      ],
      dont: ['Don’t check this one with the live test: Google says duplicate and canonical conditions aren’t tested there.'],
      verdicts: {
        content: V('look', '{These} {pages} say{s} {they} {are} the main version, but Google picked a different URL. Inspect {anyOne} to see Google’s pick.', ['inspect-canonical', 'differ', 'canonical-tag']),
        home: V('look', 'Your home page says it’s the main version, but Google picked another URL. If Google picked your home page under another form of your address, that’s fine.', ['inspect-canonical'])
      }
    },
    {
      id: 'dupSubmitted',
      normalFor: ['params'],
      label: 'Duplicate, submitted URL not selected as canonical',
      aliases: ['Submitted URL not selected as canonical'],
      group: 'not',
      meaning: 'A page you submitted, for example in your sitemap, that Google treats as a copy.',
      sources: ['G2', 'W12'],
      means: [
        'Google’s URL Inspection help lists this reason; its Page indexing help doesn’t describe it. Wix’s Site Inspection report uses it too.',
        'Going by its name, a URL you submitted, say in your sitemap, is a copy, and Google picked another URL as the main one.',
        'Treat it like the other duplicate reasons: inspect one and compare “User-declared canonical” with “Google-selected canonical”.'
      ],
      dont: [],
      verdicts: {
        content: V('look', 'Google treats {these} {pages} as {copies} of other pages and picked another URL as the main one.', ['inspect-canonical', 'merge-duplicates', 'differ'])
      }
    },
    {
      id: 'redirect',
      normalFor: ['http', 'hostVariant', 'freeWix'],
      label: 'Page with redirect',
      aliases: [],
      group: 'not',
      meaning: 'A URL that sends visitors on to another page, so Google leaves this URL out.',
      sources: ['G1', 'E_REDIRECT', 'WREDIRECT', 'H1'],
      means: [
        'These URLs send visitors on to another page. Google: “this URL will not be indexed.” The page they land on “might or might not be indexed.”',
        'Wix names the usual causes. Your free Wix URL forwards to your domain, http:// goes to https://, and the address without www goes to www. Redirects you add in the URL Redirect Manager count too. Wix says that in these cases “this behavior is normal and won’t affect your site’s ranking.”',
        'Wix also adds a redirect when you change a page’s URL slug, and a product’s unless you turned that off. Blog posts don’t get one when their slug changes. Check only that each URL lands on the right page.'
      ],
      dont: ['Don’t delete these redirects to clear the list. They send visitors and Google to the right page.'],
      verdicts: {
        content: V('normal', '{These} {pages} redirect{s} to another URL, for example after a URL slug change or a redirect added in the URL Redirect Manager. Normal, as long as {each} lands on the right page.', ['landing-check', 'redirect-manager']),
        product: V('normal', '{These} {pages} redirect{s} to another URL. On Wix, that happens when a product’s URL slug changes and Wix redirects the old one. It also happens when someone adds a redirect in the URL Redirect Manager. Normal, as long as {each} lands on the right product.', ['landing-check', 'redirect-manager']),
        category: V('normal', '{These} {pages} redirect{s} to another URL, for example after a URL change. Normal, as long as {each} lands on the right category.', ['landing-check', 'redirect-manager']),
        post: V('normal', '{These} {pages} redirect{s} to another URL. Wix doesn’t add a redirect when you change a post’s URL slug. So {they} likely {have} one from the URL Redirect Manager, or from a change to your URL structure.', ['landing-check', 'redirect-manager']),
        home: V('look', 'Your home page URL redirects somewhere else. If it goes to your main address, with or without www, that’s normal. If not, check your primary domain.', ['landing-check', 'primary-domain']),
        http: V('normal', 'Wix sends http:// URLs on to https://, and Wix lists this redirect as normal: it “won’t affect your site’s ranking.”', []),
        hostVariant: V('normal', 'Your site has one main address, {main}, and other forms of it redirect there. Wix lists redirects like non-www to www as normal: they “won’t affect your site’s ranking.”', []),
        freeWix: V('normal', 'Once a domain is connected, Wix sends your free Wix URL on to it. Wix lists this redirect as normal.', [])
      }
    },
    {
      id: 'notFound',
      normalFor: [],
      label: 'Not found (404)',
      aliases: ['Submitted URL not found (404)'],
      group: 'not',
      meaning: 'Google asked for the page and got “page not found”.',
      sources: ['G1', 'E_404', 'WVIS', 'GMOVE'],
      means: [
        'Google asked for these URLs and got “page not found”. Google says 404s are “not necessarily a problem” when a page was removed with no replacement. If a page moved, use a 301 redirect.',
        'Wix says a page gives a 404 once it’s deleted, or its URL changed without a redirect. So does a product you hide.',
        'Wix says the error clears from Search Console on its own. Redirect only the URLs that have a close replacement.'
      ],
      dont: ['Don’t redirect them all to your home page. Google may treat that as a soft 404.'],
      verdicts: {
        content: V('look', 'Google got “page not found” for {these} {pages}. Wix says that happens when a page is deleted, or its URL changed without a redirect. Fine if {they} {are} gone for good; redirect only where there’s a close replacement.', ['leave-404', 'redirect-replacement', 'no-home-redirect']),
        product: V('look', 'Google got “page not found” for {these} {pages}. Wix says that happens when a page is deleted, or its URL changed without a redirect, and when a product is hidden. Fine for products gone for good; redirect only where there’s a close replacement.', ['leave-404', 'redirect-replacement', 'hidden-product', 'no-home-redirect']),
        category: V('look', 'Google got “page not found” for {these} {pages}. Deleting a category or switching it off leaves the old URL dead, and so can changing its URL slug.', ['category-active', 'category-redirect', 'leave-404']),
        post: V('look', 'Google got “page not found” for {these} {pages}. Deleting a post leaves its old URL dead, and so does changing its URL slug, since Wix doesn’t add a redirect for that.', ['blog-redirect', 'leave-404']),
        home: V('fix', 'Google got “page not found” for your home page. Open it now: if it loads, ask Google to check again.', ['retest', 'validate'])
      }
    },
    {
      id: 'noindex',
      normalFor: ['blogTag', 'blogArchive', 'member', 'system'],
      label: 'URL marked ‘noindex’',
      shownAs: 'Excluded by ‘noindex’ tag',
      aliases: ['Excluded by ‘noindex’ tag', 'Submitted URL marked ‘noindex’', 'Excluded by noindex tag', 'noindex'],
      group: 'not',
      meaning: 'The page tells Google not to index it, with a noindex tag.',
      sources: ['G1', 'W10', 'W8', 'W9', 'W12', 'E_NOINDEX', 'W1'],
      means: [
        'These pages carry a noindex tag, which tells Google to leave them out. For a page you don’t want indexed, Google’s help says that’s the result you want.',
        'Wix sets some pages to noindex on purpose, like blog tag and archive pages, and form and calendar pages. Wix’s help pages disagree on whether member profile pages start as noindex. Search engines also can’t index password-protected or members-only pages.',
        'For a product, category or page you want in Google, check three switches: the page itself, its page type, then the whole site.'
      ],
      dont: ['Don’t turn indexing on for blog tag and archive pages just to clear this list. Wix keeps them out by default.'],
      verdicts: {
        content: V('look', '{These} {pages} tell{s} Google not to index {them}. If you want {them} in Google, check {their} SEO settings.', ['noindex-type', 'noindex-site', 'access']),
        product: V('look', '{These} {pages} tell{s} Google not to index {them}. If you want {them} in Google, turn indexing back on. If you hid {them} from search on purpose, leave {them}.', ['noindex-product', 'noindex-type', 'noindex-site', 'robots-meta', 'seo-assistant']),
        category: V('look', '{These} {pages} tell{s} Google not to index {them}. If you want {them} in Google, turn indexing back on. If you hid {them} from search on purpose, leave {them}.', ['noindex-category', 'noindex-type', 'noindex-site']),
        post: V('look', '{These} {pages} tell{s} Google not to index {them}. If you want {them} in Google, turn indexing back on. If you hid {them} from search on purpose, leave {them}.', ['noindex-post', 'noindex-type', 'noindex-site']),
        page: V('look', '{These} {pages} tell{s} Google not to index {them}. If you want {them} in Google, turn indexing back on. If you hid {them} from search on purpose, leave {them}.', ['noindex-page', 'noindex-site', 'robots-meta', 'access']),
        home: V('fix', 'Your home page tells Google not to index it. Check the whole-site switch first.', ['noindex-site', 'noindex-page']),
        program: V('look', '{These} {pages} tell{s} Google not to index {them}. Wix keeps secret online programs out of Google by default. If {these} should be public, check {their} SEO settings.', ['noindex-type']),
        other: V('look', '{These} {pages} tell{s} Google not to index {them}. Wix sets form and calendar pages to noindex by default. If you want {them} in Google, check {their} SEO settings.', ['noindex-page', 'access'])
      }
    },
    {
      id: 'soft404',
      normalFor: [],
      label: 'Soft 404',
      aliases: ['Submitted URL seems to be a Soft 404'],
      group: 'not',
      meaning: 'The page loaded, but Google thinks it looks empty or like an error.',
      sources: ['G1', 'GSTATUS', 'E_SOFT', 'GECOM', 'GMOVE'],
      means: [
        'These pages loaded without an error code, but Google thinks they look like an error or an empty page. Google asks sites to return a real 404 for pages that are truly “not found”.',
        'Wix says deleted products and blog posts can show up as soft 404s, and calls this “normal behaviour” that “will be resolved eventually.”',
        'If a page here is live and should be in Google, open it. A category with no products can look empty, and so can a page with very little on it. Google also says sending many old URLs to one unrelated page, like the home page, might be treated as a soft 404.'
      ],
      dont: ['Don’t redirect deleted products to your home page to clear this list. Google may treat those redirects as soft 404s too.'],
      verdicts: {
        content: V('look', 'Google thinks {these} {pages} look{s} empty or like an error page. If {they} {are} still live, open {them} and check what’s on {them}.', ['open-live', 'no-home-redirect']),
        product: V('look', 'If you deleted {these} {pages}, this is expected: Wix calls soft 404s for deleted products normal. If a product is still for sale, open its page: Google flags pages that look empty or like an error.', ['open-live', 'no-home-redirect']),
        post: V('look', 'If you deleted {these} {pages}, this is expected: Wix calls soft 404s for deleted posts normal. If a post is still live, open it: Google flags pages that look empty or like an error.', ['open-live', 'no-home-redirect']),
        category: V('look', 'A category with no products can look empty to Google, and Google suggests keeping empty categories out of its index.', ['category-empty', 'open-live']),
        home: V('fix', 'Google thinks your home page looks empty or like an error. Open it and check it shows real content.', ['open-live', 'home-content', 'live-test'])
      }
    },
    {
      id: 'robots',
      normalFor: ['system', 'member'],
      label: 'URL blocked by robots.txt',
      shownAs: 'Blocked by robots.txt',
      aliases: ['Blocked by robots.txt', 'Submitted URL blocked by robots.txt'],
      group: 'not',
      meaning: 'Your robots.txt file asks Google not to crawl the page.',
      sources: ['G1', 'W14', 'W1'],
      means: [
        'Your site’s robots.txt file asks Google not to crawl these URLs, so Google hasn’t read them.',
        'Wix’s help says “you don’t need to edit your robots.txt file.” If a page you want in Google is here, check first whether it’s hidden from search engines, password-protected or for members only.',
        'Google notes that robots.txt isn’t the way to keep a page out of its results. That’s what a noindex tag is for.'
      ],
      dont: ['Don’t edit robots.txt to fix this. Wix says you don’t need to; check the page’s settings instead.'],
      verdicts: {
        content: V('look', 'robots.txt asks Google not to crawl {these} {pages}. If you want {them} in Google, check {they} {arent} hidden from search engines, password-protected or for members only.', ['index-switch', 'access', 'robots-no-edit']),
        home: V('fix', 'robots.txt asks Google not to crawl your home page. Check your whole site isn’t hidden from search engines.', ['noindex-site', 'access'])
      }
    },
    {
      id: 'serverError',
      normalFor: [],
      label: 'Server error (5xx)',
      aliases: ['Submitted URL has server error (5xx)'],
      group: 'not',
      meaning: 'The server answered with an error when Google asked for the page.',
      sources: ['G1', 'E_5XX', 'G2'],
      means: [
        'Wix’s servers answered with an error when Google asked for these pages. Google says server errors “can be transient”.',
        'Open the pages now. If they load, run a live test in URL Inspection, then click Validate fix in the report. Wix suggests contacting Wix if the errors keep coming back.'
      ],
      dont: ['Don’t click Validate fix again while a check is running. Google: “Do not click Validate fix again until validation has succeeded or failed.”'],
      verdicts: {
        content: V('look', 'Wix’s servers returned an error when Google asked for {these} {pages}. Google says server errors can be temporary.', ['retest', 'validate', 'contact-wix']),
        home: V('look', 'Your home page returned a server error when Google asked for it. Open it now to check it loads.', ['retest', 'validate', 'contact-wix'])
      }
    },
    {
      id: 'redirectError',
      normalFor: [],
      label: 'Redirect error',
      aliases: ['Submitted URL has redirect error'],
      group: 'not',
      meaning: 'Google couldn’t follow the redirect: too long, a loop, or a bad URL.',
      sources: ['G1', 'GSTATUS', 'GMOVE', 'WLOOP'],
      means: [
        'Google tried to follow a redirect from these URLs and failed. Google names the usual causes: a chain that’s too long, a loop, or a bad, empty or too-long URL.',
        'Google follows up to 10 redirect hops, and advises pointing a redirect straight at the final page. Wix’s URL Redirect Manager won’t save a redirect that makes a loop.'
      ],
      dont: ['Don’t stack a new redirect on top of an old one. Point each old URL straight at the final page.'],
      verdicts: {
        content: V('fix', 'Google couldn’t follow the redirect from {these} {pages}. The chain may be too long, go in a loop, or end at a bad URL.', ['follow-redirect', 'chain-tool']),
        http: V('look', 'Google couldn’t follow Wix’s redirect from {these} {pages}. Open {anyOne} and see where it ends; if it loops, contact Wix.', ['follow-redirect', 'contact-wix']),
        hostVariant: V('look', 'Google couldn’t follow Wix’s redirect from {these} {pages}. Open {anyOne} and see where it ends; if it loops, contact Wix.', ['follow-redirect', 'contact-wix']),
        freeWix: V('look', 'Google couldn’t follow Wix’s redirect from {these} {pages}. Open {anyOne} and see where it ends; if it loops, contact Wix.', ['follow-redirect', 'contact-wix'])
      }
    },
    {
      id: 'unauthorized',
      normalFor: ['member', 'system'],
      label: 'Blocked due to unauthorized request (401)',
      aliases: ['Submitted URL returns unauthorized request (401)'],
      group: 'not',
      meaning: 'Google was asked to log in to see the page.',
      sources: ['G1', 'W1'],
      means: [
        'Google was asked to log in to see these pages, so it couldn’t read them. Google’s fix is to remove the login requirement, or to let Googlebot in.',
        'Search engines can’t index password-protected or members-only pages on Wix. If these pages are meant to be private, there’s nothing to fix.'
      ],
      dont: [],
      verdicts: {
        content: V('look', 'Google was asked to log in to see {these} {pages}. If {they} should be public, remove the password or the members-only setting.', ['access'])
      }
    },
    {
      id: 'forbidden',
      normalFor: ['member', 'system'],
      label: 'Blocked due to access forbidden (403)',
      aliases: [],
      group: 'not',
      meaning: 'The page refused Google’s visit.',
      sources: ['G1', 'E_403'],
      means: [
        'These pages refused Google’s visit. Google: “Googlebot never provides credentials, so your server is returning this error incorrectly.”',
        'Wix says its password-protected and members-only pages answer search engines with a 403, and that this is “nothing to worry about.” A product page here is unexpected.'
      ],
      dont: [],
      verdicts: {
        content: V('look', '{These} {pages} refused Google’s visit. Wix says that’s expected for password-protected and members-only pages. If {these} should be public, check {their} access settings.', ['access', 'live-test']),
        product: V('fix', '{These} {pages} refused Google’s visit. Wix expects that only for password-protected and members-only pages, so it’s unexpected on a product page.', ['access', 'live-test', 'contact-wix']),
        member: V('normal', 'Wix says members-only and password-protected pages answer search engines with a 403, and that it’s “nothing to worry about.”', [])
      }
    },
    {
      id: 'other4xx',
      normalFor: [],
      label: 'URL blocked due to other 4xx issue',
      shownAs: 'Blocked due to other 4xx issue',
      aliases: ['Blocked due to other 4xx issue', 'Submitted URL blocked due to other 4xx issue'],
      group: 'not',
      meaning: 'Google got an error starting with 4 that the other reasons don’t cover.',
      sources: ['G1', 'G2'],
      means: [
        'Google got an error starting with 4 for these URLs, one that isn’t covered by the other reasons.',
        'Run a live test in URL Inspection on one to see what Google gets now.'
      ],
      dont: [],
      verdicts: {
        content: V('look', 'Google got an error for {these} {pages} that the other reasons don’t cover.', ['live-test', 'retest'])
      }
    },
    {
      id: 'removal',
      normalFor: [],
      label: 'Blocked by page removal tool',
      aliases: [],
      group: 'not',
      meaning: 'Someone hid the page with Search Console’s Removals tool.',
      sources: ['G2', 'GPAUSE'],
      means: [
        'Someone asked Google to hide these URLs with Search Console’s Removals tool. Google’s advice: “Check this in the Temporary Removals page instead.”',
        'For a sold-out product, Google advises against hiding it. Keep the page and mark it out of stock, so people can see what’s going on.'
      ],
      dont: [],
      verdicts: {
        content: V('look', 'Someone hid {these} {pages} with Search Console’s Removals tool.', ['removals-page']),
        product: V('look', 'Someone hid {these} {pages} with Search Console’s Removals tool. For a sold-out product, Google advises keeping the page and marking it out of stock instead.', ['removals-page', 'out-of-stock'])
      }
    },
    {
      id: 'crawlAnomaly',
      normalFor: ['wixApi'],
      label: 'Crawl anomaly',
      aliases: ['Submitted URL has crawl issue'],
      group: 'older',
      meaning: 'Older name Google dropped in 2021. Wix still mentions it.',
      sources: ['G14', 'E_ANOMALY'],
      means: [
        'Google removed this reason in 2021 and now files these errors under more specific reasons. Wix’s help still covers it.',
        'Wix’s help names one URL that shows up with it, /_api/common-services/notification/invoke, and says to ignore it.'
      ],
      dont: [],
      verdicts: {
        content: V('look', 'Google stopped using this reason in 2021. Check {these} {pages} in URL Inspection to see {their} current status.', ['inspect']),
        wixApi: V('normal', '{These} start{s} with /_api/: a system address, not a page of your store. Wix’s help says to ignore this reason for /_api/common-services/notification/invoke.', [])
      }
    },
    {
      id: 'indexedBlocked',
      normalFor: [],
      label: 'Indexed, though blocked by robots.txt',
      aliases: [],
      group: 'indexed',
      meaning: 'In Google, but robots.txt stops Google reading the page.',
      sources: ['G1', 'W14', 'W10'],
      means: [
        'These pages are in Google, but your robots.txt file stops Google from reading them. Google: “robots.txt is not the correct mechanism to avoid being indexed.”',
        'Want them in Google? Turn on the page’s “Let search engines index this page” switch and publish: Wix updates robots.txt to match. Want them out? Google says robots.txt alone isn’t the way; check the page in URL Inspection.'
      ],
      dont: ['Don’t edit robots.txt to fix this. Wix says you don’t need to.'],
      verdicts: {
        content: V('look', '{These} {pages} {are} in Google, but robots.txt stops Google from reading {them}. Want {them} in Google? Turn on the index switch below and publish. Want {them} out? Leave it off and check {them} in URL Inspection.', ['index-switch', 'robots-no-edit', 'inspect'])
      }
    },
    {
      id: 'indexedNoContent',
      normalFor: [],
      label: 'Page indexed without content',
      aliases: [],
      group: 'indexed',
      meaning: 'In Google, but Google couldn’t read what’s on the page.',
      sources: ['G1', 'G2'],
      means: [
        'These pages are in Google, but Google “could not read the content.”',
        'Inspect one in URL Inspection and read the Coverage section of the indexed result. Google says a live test can’t check this reason.'
      ],
      dont: [],
      verdicts: {
        content: V('look', 'Google indexed {these} {pages} but couldn’t read what’s on {them}.', ['inspect', 'open-live']),
        product: V('fix', 'Google indexed {these} {pages} but couldn’t read what’s on {them}. A product page with no readable content can’t show what you sell.', ['inspect', 'open-live', 'contact-wix']),
        home: V('fix', 'Google indexed your home page but couldn’t read what’s on it.', ['inspect', 'open-live', 'contact-wix'])
      }
    }
  ].map(reason => Object.freeze({ ...reason, normalFor: Object.freeze(reason.normalFor), aliases: Object.freeze(reason.aliases), means: Object.freeze(reason.means), dont: Object.freeze(reason.dont), verdicts: Object.freeze(reason.verdicts) }));

  const REASON_GROUPS = Object.freeze([
    ['not', 'Why pages aren’t indexed'],
    ['older', 'Older name, still used by Wix'],
    ['indexed', 'Indexed, with a warning']
  ]);

  // Labels arrive with en dashes, straight quotes or extra spaces.
  function labelKey(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .replace(/[‘’‚‛′`´]/g, "'")
      .replace(/[“”„″]/g, '"')
      .replace(/[‐‑‒–—―−]/g, '-')
      .replace(/\s*-\s*/g, ' - ')
      .replace(/\s+/g, ' ')
      .replace(/[.:]+$/, '')
      .trim();
  }
  const REASON_INDEX = new Map();
  REASONS.forEach(reason => {
    REASON_INDEX.set(reason.id.toLowerCase(), reason);
    [reason.label, ...reason.aliases].forEach(name => REASON_INDEX.set(labelKey(name), reason));
  });
  function findReason(value) {
    if (value && typeof value === 'object' && value.id) return REASON_INDEX.get(value.id.toLowerCase()) || null;
    const text = String(value == null ? '' : value);
    return REASON_INDEX.get(text.toLowerCase()) || REASON_INDEX.get(labelKey(text)) || null;
  }

  function rule(reason, type) {
    const own = reason.verdicts[type];
    if (own) return own;
    const global = TYPE_VERDICTS[type];
    if (global && (reason.group !== 'indexed' || INDEXED_GLOBALS.has(type))) return global;
    return reason.verdicts.content;
  }

  // ---- Reading what was pasted ------------------------------------------------

  const DATE = /^(?:\d{4}-\d{1,2}-\d{1,2}(?:[ t]\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?z?)?|\d{1,2}[./-]\d{1,2}[./-]\d{2,4}|(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\.? \d{1,2},? \d{4}|\d{1,2} (?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\.? \d{4})$/i;
  const HEADER = /^(?:urls?|page urls?|addresses|address|pages?|links?|top pages|example urls?|examples?|landing pages?)$/i;
  // Lines that are column names or a report's labels, not data.
  const HEADERISH = /^(?:(?:urls?|pages?|last crawled|examples?|status|addresses|address|links?)\s*)+$/i;
  const DOMAIN = /^((?:[\p{L}\p{N}](?:[\p{L}\p{N}-]*[\p{L}\p{N}])?\.)+\p{L}{2,}\.?)(:\d{1,5})?([/?#].*)?$/u;
  const RELATIVE = /^[\p{L}\p{N}._~%!$&'()*+,;=:@-]+\/\S*$/u;
  const HOST_OK = /^[\p{L}\p{N}.-]+$/u;

  // One pasted value as { protocol, host, path, query } or null when it isn't
  // a web address. Fragments are dropped: they never reach the server.
  function readAddress(input) {
    let text = String(input == null ? '' : input).replace(/[\u200b-\u200d\u2060\ufeff]/g, '').trim();
    text = text.replace(/^[<("'“‘[]+/, '').replace(/[>)"'”’\],;]+$/, '').trim();
    text = text.replace(/^view-source:/i, '');
    if (!text || /\s/.test(text)) return null;
    let protocol = 'https';
    let host = '';
    let rest = '';
    let relative = false;
    let match = /^(https?):\/\/([^/?#]*)(.*)$/i.exec(text);
    if (match) { protocol = match[1].toLowerCase(); host = match[2]; rest = match[3]; }
    else if ((match = /^\/\/([^/?#]+)(.*)$/.exec(text))) { host = match[1]; rest = match[2]; }
    else if (text[0] === '/') { rest = text; relative = true; }
    else if ((match = DOMAIN.exec(text))) {
      // "robots.txt" or "price-list.pdf" on its own is a file, not a website.
      const ending = match[1].replace(/\.$/, '').split('.').pop().toLowerCase();
      if (!match[2] && !/^\//.test(match[3] || '') && FILE_LIKE.has(ending) && !/^www\./i.test(text)) { rest = `/${text}`; relative = true; }
      else { host = match[1]; rest = match[3] || ''; }
    }
    else if (/^[a-z][a-z0-9+.-]*:/i.test(text)) return null;
    else if (RELATIVE.test(text)) { rest = `/${text}`; relative = true; }
    else return null;
    if (!relative) {
      host = host.replace(/^[^@]*@/, '').replace(/:\d*$/, '').replace(/\.$/, '').toLowerCase();
      if (!host || !HOST_OK.test(host) || !host.includes('.')) return null;
    }
    const hash = rest.indexOf('#');
    if (hash !== -1) rest = rest.slice(0, hash);
    const at = rest.indexOf('?');
    let path = at === -1 ? rest : rest.slice(0, at);
    const query = at === -1 ? '' : rest.slice(at + 1);
    if (!path) path = '/';
    if (path[0] !== '/') path = `/${path}`;
    return { protocol, host, path, query, relative };
  }

  const keyOf = u => `${u.relative ? '' : `${u.protocol}://${u.host}`}${u.path}${u.query ? `?${u.query}` : ''}`;

  function splitSimple(line, delimiter) {
    if (shared && shared.parseDelimited) return (shared.parseDelimited(line, delimiter)[0] || []);
    return line.split(delimiter);
  }
  const unquote = value => tidy(value).replace(/^"(.*)"$/, '$1');

  // A date at the end of a line, after a comma, semicolon or tab.
  const TRAILING_DATE = new RegExp(`[,;\\t]\\s*(?:${DATE.source.slice(1, -1)})\\s*$`, 'i');

  // A row of cells that is data: a URL beside a date (or a count), with any
  // other text cells around them.
  function isDataRow(cells) {
    if (cells.length < 2) return false;
    const at = cells.findIndex(cell => cell && !DATE.test(cell) && readAddress(cell));
    if (at === -1) return false;
    const rest = cells.filter((cell, index) => index !== at && cell);
    return rest.some(cell => DATE.test(cell)) || (rest.length > 0 && rest.every(cell => /^\d+$/.test(cell)));
  }

  // A table pasted from Sheets or Excel, or Search Console's CSV export: tabs
  // anywhere, or a header or a URL beside a date in one of the first lines
  // (a report's title above the table doesn't hide it).
  function tableDelimiter(lines, input) {
    const sample = [];
    for (const line of lines) {
      if (line.trim()) sample.push(line);
      if (sample.length === 6) break;
    }
    if (!sample.length) return null;
    if (sample.some(line => line.includes('\t'))) return '\t';
    for (const delimiter of [',', ';']) {
      for (const line of sample) {
        if (!line.includes(delimiter)) continue;
        const cells = splitSimple(line, delimiter).map(unquote);
        if (cells.some(cell => HEADER.test(cell)) || isDataRow(cells)) return delimiter;
      }
    }
    if (/^"/.test(sample[0].trim()) && shared && shared.detectDelimiter) return shared.detectDelimiter(input);
    return null;
  }

  // Everything pasted or opened, as unique URLs in their pasted order, with
  // counts for what was skipped, repeated or cut.
  function parseUrls(text) {
    let input = String(text == null ? '' : text).replace(/^﻿/, '');
    const out = { urls: [], skipped: 0, skippedSamples: [], dates: 0, repeats: 0, header: null, truncated: false, notSorted: 0, cut: false, table: false };
    if (input.length > MAX_CHARS) {
      input = input.slice(0, MAX_CHARS);
      const lastBreak = Math.max(input.lastIndexOf('\n'), input.lastIndexOf('\r'));
      if (lastBreak > 0) input = input.slice(0, lastBreak);
      out.cut = true;
    }
    if (!input.trim()) return out;
    const seen = new Set();
    const overflow = new Set();
    const add = raw => {
      const value = tidy(raw);
      if (!value) return;
      if (DATE.test(value)) { out.dates += 1; return; }
      const u = readAddress(value);
      if (!u) {
        // Column names, a report's labels and the reason's own name aren't data.
        if (!HEADERISH.test(value) && !findReason(value)) {
          out.skipped += 1;
          if (out.skippedSamples.length < 3) out.skippedSamples.push(value.length > 60 ? `${value.slice(0, 59)}…` : value);
        }
        return;
      }
      const key = keyOf(u);
      if (seen.has(key)) { out.repeats += 1; return; }
      if (out.urls.length >= MAX_URLS) {
        out.truncated = true;
        if (!overflow.has(key)) { overflow.add(key); out.notSorted += 1; }
        return;
      }
      seen.add(key);
      u.raw = value;
      out.urls.push(u);
    };
    let lines = input.split(/\r\n|\n|\r/);
    // Excel's "sep=," first line names the delimiter.
    let delimiter = null;
    const first = lines.findIndex(line => line.trim());
    const sep = first === -1 ? null : /^\s*"?sep=(.)"?\s*$/i.exec(lines[first]);
    if (sep) {
      delimiter = sep[1];
      lines = lines.slice(first + 1);
      input = lines.join('\n');
    }
    delimiter = delimiter || tableDelimiter(lines, input);
    if (delimiter) {
      out.table = true;
      const rows = shared && shared.parseDelimited ? shared.parseDelimited(input, delimiter) : lines.map(line => line.split(delimiter));
      const filled = rows.filter(row => row.some(cell => tidy(cell)));
      let column = -1;
      let start = 0;
      // A header row above the data: read its URL column, from the row after it.
      for (let index = 0; index < Math.min(filled.length, 6); index += 1) {
        const head = filled[index].map(unquote);
        const named = head.findIndex(cell => HEADER.test(cell));
        if (named !== -1) { column = named; out.header = head[named]; start = index + 1; break; }
        if (head.some(cell => cell && !DATE.test(cell) && readAddress(cell))) break;
      }
      if (column === -1) {
        // No header: the column where most cells read as addresses.
        const top = filled.slice(0, 30);
        const width = Math.max(0, ...top.map(row => row.length));
        let best = 0;
        for (let index = 0; index < width; index += 1) {
          const score = top.filter(row => {
            const cell = unquote(row[index] || '');
            return cell && !DATE.test(cell) && readAddress(cell);
          }).length;
          if (score > best) { best = score; column = index; }
        }
        if (column === -1) column = 0;
      }
      for (let index = start; index < filled.length; index += 1) add(filled[index][column] || '');
      return out;
    }
    lines.forEach(line => {
      const value = tidy(line).replace(TRAILING_DATE, '');
      if (!value) return;
      // Words around a URL, or URLs glued together with a comma or semicolon.
      const tokens = value.split(/\s+|[,;]+(?=(?:https?:\/\/|www\.))/i).filter(Boolean);
      if (tokens.length > 1) {
        const found = tokens.filter(token => !DATE.test(token) && readAddress(token));
        if (found.length) { found.forEach(add); return; }
      }
      add(value);
    });
    return out;
  }

  // ---- Which site the URLs belong to ----------------------------------------

  const TWO_LEVEL = new Set(['co.uk', 'org.uk', 'me.uk', 'ac.uk', 'ltd.uk', 'plc.uk', 'com.au', 'net.au', 'org.au', 'co.nz', 'org.nz', 'co.za', 'com.br', 'com.mx', 'co.jp', 'co.in', 'com.sg', 'com.hk', 'co.il', 'com.tr', 'com.ar', 'co.kr', 'com.my', 'com.ph']);
  // Free Wix addresses: name.wixsite.com/site (Wix Editor),
  // name.wixstudio.com/site and name.wixstudio.io/site (Wix Studio), and the
  // older name.editorx.io/site.
  const isFree = host => /\.(?:wixsite\.com|wixstudio\.com|wixstudio\.io|editorx\.io)$/.test(host || '');
  const bare = host => String(host || '').replace(/^www\./, '');
  // The registrable domain, roughly. Every free Wix site is its own site.
  function baseDomain(host) {
    if (!host) return '';
    const parts = host.split('.');
    if (isFree(host)) return parts.slice(-3).join('.');
    if (parts.length <= 2) return host;
    return TWO_LEVEL.has(parts.slice(-2).join('.')) ? parts.slice(-3).join('.') : parts.slice(-2).join('.');
  }

  // "www.yourstore.com", "https://yourstore.com/" or "name.wixsite.com/site".
  // A free Wix URL keeps its first path part: it is the site's own name.
  function readSite(value) {
    const text = tidy(value);
    if (!text) return null;
    const u = readAddress(text);
    if (!u || u.relative) return { error: true };
    const first = u.path.split('/').filter(Boolean)[0];
    return { host: u.host, basePath: isFree(u.host) && first ? `/${decodeSafe(first).toLowerCase()}` : '' };
  }

  // The main address: the one typed, or a guess from the list. A custom
  // domain has two forms, with and without www, so a guess comes with both as
  // choices. Under "Page with redirect" the guess is the www form: Wix sends
  // the address without www on to www, so those are the URLs that redirect.
  function siteContext(siteValue, urls = [], options = {}) {
    const ctx = { mainHost: null, base: '', basePath: '', given: false, inferred: false, siteError: false, choices: [], why: '', counts: {} };
    const site = readSite(siteValue);
    if (site && site.error) ctx.siteError = true;
    if (site && !site.error) {
      ctx.mainHost = site.host;
      ctx.basePath = site.basePath;
      ctx.given = true;
    } else {
      const counts = new Map();
      const secure = new Map();
      urls.forEach(u => {
        if (u.relative) return;
        counts.set(u.host, (counts.get(u.host) || 0) + 1);
        if (u.protocol === 'https') secure.set(u.host, (secure.get(u.host) || 0) + 1);
      });
      if (counts.size) {
        const hosts = [...counts.keys()];
        const custom = hosts.filter(host => !isFree(host));
        const pool = custom.length ? custom : hosts;
        const byBase = new Map();
        pool.forEach(host => byBase.set(baseDomain(host), (byBase.get(baseDomain(host)) || 0) + counts.get(host)));
        const base = [...byBase].sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))[0][0];
        const www = `www.${base}`;
        const family = pool.filter(host => host === base || host === www);
        const candidates = family.length ? family : pool.filter(host => baseDomain(host) === base);
        const rank = host => [secure.get(host) || 0, counts.get(host)];
        candidates.sort((a, b) => rank(b)[0] - rank(a)[0] || rank(b)[1] - rank(a)[1] || (a < b ? -1 : 1));
        ctx.mainHost = candidates[0];
        ctx.inferred = true;
        if (family.length && !isFree(base)) {
          if (options.redirect) { ctx.mainHost = www; ctx.why = 'redirect'; }
          else ctx.why = family.length === 2 ? 'both' : 'only';
          ctx.choices = [ctx.mainHost, ctx.mainHost === www ? base : www];
        }
        if (isFree(ctx.mainHost)) {
          // The site's own name: the first part most of its URLs share.
          const firsts = new Map();
          urls.forEach(u => {
            if (u.relative || u.host !== ctx.mainHost) return;
            const first = decodeSafe(u.path.split('/').filter(Boolean)[0] || '').toLowerCase();
            if (first) firsts.set(first, (firsts.get(first) || 0) + 1);
          });
          const top = [...firsts].sort((a, b) => b[1] - a[1])[0];
          if (top) ctx.basePath = `/${top[0]}`;
        }
        ctx.counts = Object.fromEntries(counts);
      }
    }
    ctx.base = baseDomain(ctx.mainHost);
    return ctx;
  }

  // ---- Classifying -----------------------------------------------------------

  function normalizePrefix(value) {
    let text = tidy(value).toLowerCase();
    if (!text) return '';
    const full = /^[a-z][a-z0-9+.-]*:\/\/[^/]*(.*)$/i.exec(text);
    if (full) text = full[1];
    text = text.split('{')[0].split(/[?#]/)[0];
    return text.split('/').map(part => decodeSafe(part.trim())).filter(Boolean).slice(0, 3).join('/');
  }

  // Wix's prefixes plus any the visitor changed in SEO Settings (products and
  // blog posts are the two Wix lets you change). Longer prefixes are tried
  // first, so /blog/tags/… beats a custom "blog" post prefix; at the same
  // length, custom ones come before Wix's.
  function prefixTable(prefixes = {}) {
    const custom = [];
    ['product', 'post'].forEach(type => {
      const prefix = normalizePrefix(prefixes && prefixes[type]);
      const segs = prefix ? prefix.split('/') : [];
      const isDefault = PATH_TYPES.some(entry => entry.type === type && entry.segs.join('/') === prefix);
      if (segs.length && !isDefault) custom.push({ type, segs });
    });
    return custom.concat(PATH_TYPES).sort((a, b) => b.segs.length - a.segs.length);
  }

  function matchPrefix(segs, table) {
    const hit = table.find(entry => segs.length > entry.segs.length && entry.segs.every((part, index) => segs[index] === part));
    return hit ? hit.type : null;
  }

  // A free Wix URL is name.wixsite.com/site-name/…: the first part is the
  // site's own name, whatever it is (even "blog"), so it's dropped.
  function segmentsOf(u, ctx) {
    const segs = u.path.split('/').filter(Boolean).map(part => decodeSafe(part).toLowerCase());
    if (!segs.length) return segs;
    if (!u.relative && isFree(u.host)) return segs.slice(1);
    if (u.relative && isFree(ctx.mainHost) && ctx.basePath && segs[0] === ctx.basePath.slice(1)) return segs.slice(1);
    return segs;
  }

  function hasParams(query) {
    if (!query) return false;
    return query.split('&').some(part => {
      const key = decodeSafe(part.split('=')[0]).trim().toLowerCase();
      return key && key !== 'lang';
    });
  }

  // The page type of one parsed URL. Address problems come first (another
  // site, the free Wix URL, http://, www or not), because they explain the
  // status better than the page behind them.
  function classify(u, ctx, table = PATH_TYPES) {
    if (!u.relative && ctx.mainHost) {
      const main = ctx.mainHost;
      if (u.host !== main) {
        if (isFree(u.host) && !isFree(main)) return 'freeWix';
        if (bare(u.host) !== bare(main)) return !isFree(main) && baseDomain(u.host) === baseDomain(main) ? 'subdomain' : 'otherHost';
        return u.protocol === 'http' ? 'http' : 'hostVariant';
      }
    }
    if (!u.relative && u.protocol === 'http') return 'http';
    let segs = segmentsOf(u, ctx);
    // A language folder in front (/fr, /fr/about, /fr/product-page/…) doesn't
    // change the type: Wix Multilingual puts each language in one by default.
    if (segs.length && isLang(segs[0]) && !matchPrefix(segs, table)) segs = segs.slice(1);
    if (segs[0] === '_api') return 'wixApi';
    const last = segs[segs.length - 1] || '';
    const ext = /\.([a-z0-9]{2,5})$/.exec(last);
    if (ext && FILE_TYPES.has(ext[1])) return 'file';
    // Search results and checkout carry ? parts of their own; they stay system pages.
    if (segs.length && SYSTEM_WORDS.has(segs[0])) return 'system';
    if (hasParams(u.query)) return 'params';
    if (!segs.length) return 'home';
    const type = matchPrefix(segs, table);
    if (type) return type;
    return segs.length === 1 ? 'page' : 'other';
  }

  function typeInfo(type, ctx) {
    const info = TYPES[type] || TYPES.other;
    if (type === 'hostVariant' && ctx && ctx.mainHost && !/^www\./.test(ctx.mainHost)) {
      return { ...info, label: 'URLs with www', one: 'URL with www', many: 'URLs with www', a: 'a URL with www' };
    }
    return info;
  }

  // Public: the page type of one URL, for the tests and the other tools.
  // `site` is a site address ("www.yourstore.com"), a siteContext, or empty.
  function pageType(url, site, options = {}) {
    const u = typeof url === 'string' ? readAddress(url) : url;
    if (!u) return null;
    const ctx = site && typeof site === 'object' && 'mainHost' in site ? site : siteContext(site, [u]);
    const type = classify(u, ctx, prefixTable(options.prefixes));
    return { type, label: typeInfo(type, ctx).label, protocol: u.protocol, host: u.host, path: u.path, query: u.query, relative: u.relative };
  }

  // ---- Decoding --------------------------------------------------------------

  // Fit a sentence to one URL or many: {these} {pages} → "this product page".
  function fill(text, tokens) {
    const one = tokens.count === 1;
    const words = {
      these: one ? 'this' : 'these',
      These: one ? 'This' : 'These',
      pages: one ? tokens.one : tokens.many,
      page: tokens.one,
      them: one ? 'it' : 'them',
      they: one ? 'it' : 'they',
      They: one ? 'It' : 'They',
      are: one ? 'is' : 'are',
      were: one ? 'was' : 'were',
      arent: one ? 'isn’t' : 'aren’t',
      their: one ? 'its' : 'their',
      copies: one ? 'a copy' : 'copies',
      copyOf: one ? 'a copy of one of your pages' : 'copies of your pages',
      have: one ? 'has' : 'have',
      s: one ? 's' : '',
      each: one ? 'it' : 'each',
      many: tokens.many,
      paged: one ? 'a paged list' : 'paged lists',
      anyOne: one ? 'it' : 'one',
      few: one ? 'it' : 'a few',
      sitemap: tokens.sitemap || 'one file per page type, such as /store-products-sitemap.xml',
      main: tokens.main ? esc(tokens.main) : 'your main address',
      host: tokens.host ? esc(tokens.host) : 'another address'
    };
    return String(text).replace(/\{(\w+)\}/g, (match, key) => (key in words ? words[key] : match));
  }

  function shareOf(count, total) {
    const share = total ? Math.round((count / total) * 100) : 0;
    return { share, shareText: share === 0 && count > 0 ? '<1%' : `${share}%` };
  }

  function fullUrl(u, ctx) {
    const query = u.query ? `?${u.query}` : '';
    if (!u.relative) return `${u.protocol}://${u.host}${u.path}${query}`;
    return ctx.mainHost ? `https://${ctx.mainHost}${u.path}${query}` : `${u.path}${query}`;
  }

  // What a card shows for one URL, what Copy puts on the clipboard, and, for
  // copies and other forms of the address, the URL that matters instead.
  function exampleOf(u, type, ctx) {
    const query = u.query ? `?${u.query}` : '';
    const onMain = u.relative || (u.host === ctx.mainHost && u.protocol === 'https');
    const copy = fullUrl(u, ctx);
    let note = '';
    if (type === 'params') note = `Original: ${onMain ? u.path : `${u.protocol}://${u.host}${u.path}`}`;
    else if (type === 'http') note = `Check instead: https://${ctx.mainHost && bare(u.host) === bare(ctx.mainHost) ? ctx.mainHost : u.host}${u.path}${query}`;
    else if (type === 'hostVariant' && ctx.mainHost) note = `Check instead: https://${ctx.mainHost}${u.path}${query}`;
    return { display: onMain ? `${u.path}${query}` : copy, copy, note };
  }

  function mostCommonHost(urls) {
    const counts = new Map();
    urls.forEach(u => { if (!u.relative) counts.set(u.host, (counts.get(u.host) || 0) + 1); });
    const top = [...counts].sort((a, b) => b[1] - a[1])[0];
    return top ? top[0] : '';
  }

  function buildGroup(reason, type, urls, total, ctx) {
    const info = typeInfo(type, ctx);
    const verdict = rule(reason, type);
    const tokens = { count: urls.length, one: info.one, many: info.many, sitemap: info.sitemap, main: ctx.mainHost, host: mostCommonHost(urls) };
    let stepIds = verdict.steps.map(id => stepIdFor(id, type));
    if (type === 'other' && !stepIds.includes('custom-prefix')) stepIds = ['custom-prefix', ...stepIds];
    stepIds = stepIds.filter((id, index) => STEPS[id] && stepIds.indexOf(id) === index);
    return {
      type,
      label: info.label,
      one: info.one,
      many: info.many,
      a: info.a,
      hint: info.hint,
      count: urls.length,
      ...shareOf(urls.length, total),
      level: verdict.level,
      text: fill(verdict.text, tokens),
      stepIds,
      steps: stepIds.map(id => ({ id, text: fill(STEPS[id].text, tokens), sources: STEPS[id].sources })),
      tokens,
      examples: urls.slice(0, EXAMPLES).map(u => exampleOf(u, type, ctx)),
      urls
    };
  }

  function overallVerdict(groups, total) {
    if (!total) return { level: 'normal', text: '' };
    const sum = level => groups.filter(group => group.level === level).reduce((n, group) => n + group.count, 0);
    const fix = sum('fix');
    const look = sum('look');
    const normal = sum('normal');
    const isAre = n => (n === 1 ? 'is' : 'are');
    if (total === 1) {
      const group = groups[0];
      const subject = group.type === 'home' ? 'Your home page' : `This ${group.one}`;
      if (group.level === 'fix') return { level: 'fix', text: `${subject} needs fixing.` };
      if (group.level === 'look') return { level: 'look', text: `${subject} is worth a look.` };
      return { level: 'normal', text: 'This one is normal on Wix. Nothing to fix.' };
    }
    // One kind of page only: say what they are and what to do in one go.
    if (groups.length === 1) {
      const only = groups[0];
      if (only.level === 'fix') return { level: 'fix', text: `${allOf(total)} are ${only.many} that need fixing.` };
      if (only.level === 'look') return { level: 'look', text: `${allOf(total)} are ${only.many} worth a look.` };
      return { level: 'normal', text: `${allOf(total)} are normal on Wix. Nothing to fix.` };
    }
    const counted = (n, group) => (n === 1 ? group.a : `${num(n)} ${group.many}`);
    // The groups at a level, named the way the count reads best.
    const mostly = (count, level) => {
      const own = groups.filter(group => group.level === level);
      const [lead, second] = own;
      if (own.length === 1) return count === 1 ? `: ${lead.a}` : `: all ${lead.many}`;
      if (own.length === 2) return `: ${counted(lead.count, lead)} and ${counted(second.count, second)}`;
      if (lead.count / count > 0.5) return `, most of them ${lead.many}`;
      return `, mainly ${counted(lead.count, lead)} and ${counted(second.count, second)}`;
    };
    const others = n => (n === 1 ? 'The other one is normal on Wix.' : `The other ${num(n)} are normal on Wix.`);
    if (fix) {
      const rest = [look ? `${look === 1 ? 'One more is' : `${num(look)} more are`} worth a look.` : '', normal ? (look ? `${num(normal)} ${isAre(normal)} normal on Wix.` : others(normal)) : ''].filter(Boolean).join(' ');
      return { level: 'fix', text: `${num(fix)} of ${num(total)} need${fix === 1 ? 's' : ''} fixing${mostly(fix, 'fix')}.${rest ? ` ${rest}` : ''}` };
    }
    if (look) {
      const lead = look === total ? `${allOf(total)} are worth a look` : `${num(look)} of ${num(total)} ${isAre(look)} worth a look`;
      return { level: 'look', text: `${lead}${mostly(look, 'look')}.${normal ? ` ${others(normal)}` : ''}` };
    }
    return { level: 'normal', text: `${allOf(total)} are normal on Wix. Nothing to fix.` };
  }

  // The to-do list: steps from the groups that need work, fixes first, one
  // step per group per round, so a big group can't crowd out the others.
  function firstStepsOf(groups) {
    const working = groups.filter(group => group.level !== 'normal').sort((a, b) => LEVEL_RANK[a.level] - LEVEL_RANK[b.level] || b.count - a.count);
    const picked = new Map();
    const rounds = Math.max(0, ...working.map(group => group.stepIds.length));
    for (let round = 0; round < rounds; round += 1) {
      for (const group of working) {
        const id = group.stepIds[round];
        if (!id) continue;
        if (picked.has(id)) { picked.get(id).groups.push(group); continue; }
        if (picked.size < 5) picked.set(id, { id, groups: [group] });
      }
    }
    // Groups that never reached a picked step still use the shared ones.
    working.forEach(group => group.stepIds.forEach(id => {
      const entry = picked.get(id);
      if (entry && !entry.groups.includes(group)) entry.groups.push(group);
    }));
    return [...picked.values()].map(entry => {
      const tokens = entry.groups.length === 1 ? entry.groups[0].tokens : { count: 2, one: 'page', many: 'pages', main: entry.groups[0].tokens.main, host: entry.groups[0].tokens.host };
      return { id: entry.id, text: fill(STEPS[entry.id].text, tokens), sources: STEPS[entry.id].sources, groups: entry.groups.map(group => (group.count === 1 ? group.a : group.many)) };
    });
  }

  function sentenceOf(reason, groups, total) {
    if (!total) return '';
    const lead = `You pasted ${plural(total, 'URL')} under “${reason.label}”.`;
    const top = groups[0];
    if (groups.length === 1) return `${lead} ${total === 1 ? `It’s ${top.a}.` : `${allOf(total)} are ${top.many}.`}`;
    return `${lead} ${num(top.count)} (${top.shareText}) ${top.count === 1 ? `is ${top.a}` : `are ${top.many}`}.`;
  }

  // decode({ reason, text | urls | parsed, site, prefixes }) → everything the
  // page shows: groups by page type with verdicts, the summary, the to-do.
  function decode(input = {}) {
    const reason = findReason(input.reason) || REASONS[0];
    const parsed = input.parsed || parseUrls(Array.isArray(input.urls) ? input.urls.join('\n') : input.text);
    const ctx = siteContext(input.site, parsed.urls, { redirect: reason.id === 'redirect' });
    const table = prefixTable(input.prefixes);
    const byType = new Map();
    parsed.urls.forEach(u => {
      const type = classify(u, ctx, table);
      if (!byType.has(type)) byType.set(type, []);
      byType.get(type).push(u);
    });
    const total = parsed.urls.length;
    const groups = [...byType].map(([type, urls]) => buildGroup(reason, type, urls, total, ctx));
    groups.sort((a, b) => b.count - a.count || LEVEL_RANK[a.level] - LEVEL_RANK[b.level] || TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type));
    // Categorical colour slots follow the groups' order in the bar. Five at
    // most: the rest share one grey "Other types" segment, so the palette's
    // green and red never sit beside the Normal and Fix chips.
    groups.forEach((group, index) => { group.slot = index < 5 ? index + 1 : 0; });
    const onSite = ctx.given ? groups.filter(group => group.type !== 'otherHost').reduce((n, group) => n + group.count, 0) : total;
    return {
      reason,
      total,
      parse: parsed,
      site: { mainHost: ctx.mainHost, basePath: ctx.basePath, given: ctx.given, inferred: ctx.inferred, choices: ctx.choices, why: ctx.why, error: ctx.siteError, noneOnSite: Boolean(ctx.given && total && !onSite) },
      groups,
      verdict: overallVerdict(groups, total),
      sentence: sentenceOf(reason, groups, total),
      firstSteps: firstStepsOf(groups),
      means: reason.means,
      dont: reason.dont
    };
  }

  const ENTITIES = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" };
  const plainText = html => String(html).replace(/<[^>]+>/g, '').replace(/&(?:amp|lt|gt|quot|#39);/g, entity => ENTITIES[entity]);

  // The text that "Copy summary" and the download carry. URLs only when asked.
  function describe(input, options = {}) {
    const result = input && Array.isArray(input.groups) && input.reason ? input : decode(input);
    if (!result.total) return '';
    const lines = ['Wix indexing decoder, by myuser.ai', `Reason in Search Console: ${result.reason.label}`];
    if (result.site.mainHost) lines.push(`Site: ${result.site.mainHost}${result.site.basePath}${result.site.inferred ? ' (guessed from the list)' : ''}`);
    lines.push('', result.sentence, result.verdict.text, '', 'By page type');
    result.groups.forEach(group => {
      lines.push(`- ${group.label}: ${group.count} (${group.shareText}), ${LEVEL_WORD[group.level]}`);
      lines.push(`  ${plainText(group.text)}`);
      if (options.includeUrls) group.urls.forEach(u => lines.push(`  ${fullUrl(u, result.site)}`));
    });
    if (result.firstSteps.length) {
      lines.push('', 'What to do first');
      result.firstSteps.forEach((step, index) => lines.push(`${index + 1}. ${plainText(step.text)}`));
    }
    if (result.dont.length) {
      lines.push('', 'Don’t');
      result.dont.forEach(item => lines.push(`- ${plainText(item)}`));
    }
    lines.push('', 'Sorted in your browser from the list you pasted. The decoder can’t see Google’s data.', `https://myuser.ai${META.path}`);
    return `${lines.join('\n')}\n`;
  }

  // ---- Made-up samples ---------------------------------------------------------

  // One fictional store. Its .example address can't belong to anyone.
  const STORE = 'Juniper & Oak Candle Co.';
  const SITE = 'https://www.juniperandoak.example';
  const BARE = 'https://juniperandoak.example';
  const HTTP = 'http://www.juniperandoak.example';
  const HTTP_BARE = 'http://juniperandoak.example';
  const PRODUCTS = ['cedar-and-sage-soy-candle', 'beeswax-taper-pair', 'amber-jar-candle-8-oz', 'lavender-linen-room-spray', 'black-fig-reed-diffuser', 'wooden-wick-tumbler', 'travel-tin-trio', 'citrus-grove-wax-melts', 'sea-salt-and-moss-candle', 'smoked-vanilla-pillar', 'pine-needle-votive-set', 'rose-clay-incense-holder', 'brass-wick-trimmer', 'matchbox-gift-set', 'ceramic-candle-snuffer', 'oak-moss-three-wick-candle', 'honey-glow-tealights', 'winter-spruce-candle', 'peppermint-bark-wax-melts', 'pumpkin-chai-candle', 'unscented-pillar-candle', 'soy-wax-flakes-refill', 'linen-dust-cover', 'gift-card', 'sandalwood-tumbler', 'green-tea-reed-diffuser', 'midnight-jasmine-candle', 'coastal-driftwood-candle', 'orange-clove-candle', 'birch-candle-tray', 'candle-making-kit', 'eucalyptus-mint-tin', 'cozy-cabin-candle', 'wildflower-meadow-candle', 'tobacco-and-leather-candle', 'apple-orchard-votive', 'spiced-cranberry-candle', 'vanilla-bean-wax-melts', 'cotton-wick-refills', 'glass-cloche-cover'];
  const OLD_PRODUCTS = ['pumpkin-spice-candle-2025', 'holiday-advent-candle-set', 'valentine-heart-votive', 'summer-citronella-bucket', 'limited-gingerbread-candle', 'mothers-day-gift-box', 'spring-bloom-trio', 'halloween-black-pillar', 'snowfall-wax-melts', 'cherry-blossom-tin', 'autumn-harvest-set', 'new-year-gold-candle', 'tropical-mango-candle', 'christmas-tree-ornament-candle', 'easter-pastel-tapers'];
  const RENAMED = ['cedar-sage-candle', 'beeswax-tapers', 'amber-candle-8oz', 'lavender-room-spray', 'fig-diffuser', 'wood-wick-tumbler', 'travel-tins', 'citrus-wax-melts', 'sea-salt-candle'];
  const POSTS = ['how-to-trim-a-candle-wick', 'soy-vs-beeswax', 'why-candles-tunnel', 'candle-care-guide', 'scent-layering-ideas', 'our-wax-sourcing', 'holiday-gift-guide', 'first-burn-tips', 'reed-diffuser-tips', 'pouring-day'];
  const CATEGORIES = ['gift-sets', 'wax-melts', 'reed-diffusers', 'seasonal', 'accessories', 'tealights', 'sale', 'all-products'];
  const at = path => `${SITE}${path}`;
  const product = slug => at(`/product-page/${slug}`);
  const post = slug => at(`/post/${slug}`);
  const category = slug => at(`/category/${slug}`);

  // A fixed shuffle, so a sample reads like a real export, not sorted by type.
  function shuffle(list, seed) {
    const out = list.slice();
    let state = seed >>> 0;
    for (let index = out.length - 1; index > 0; index -= 1) {
      state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
      const swap = state % (index + 1);
      [out[index], out[swap]] = [out[swap], out[index]];
    }
    return out;
  }

  const RECIPES = {
    crawled: [
      ...PRODUCTS.slice(0, 37).map(product),
      ...POSTS.slice(0, 7).map(post),
      product('cedar-and-sage-soy-candle?fb=1'), product('beeswax-taper-pair?fb=1'), category('all-products?page=2'), category('all-products?page=3'), category('gift-sets?sort=price_ascending'), at('/blog?page=2'),
      ...CATEGORIES.slice(0, 5).map(category),
      at('/candle-care'), at('/wholesale'), at('/faq'), at('/shipping-and-returns'), at('/about')
    ],
    discovered: [
      ...PRODUCTS.slice(12, 30).map(product),
      ...POSTS.slice(4, 8).map(post),
      ...CATEGORIES.slice(4, 7).map(category),
      category('all-products?page=4'), category('sale?sort=newest'),
      at('/wholesale')
    ],
    alternate: [
      ...PRODUCTS.slice(0, 6).map(slug => product(`${slug}?fb=1`)),
      category('all-products?page=2'), category('all-products?page=3'), category('all-products?page=4'), category('all-products?page=5'),
      category('gift-sets?sort=price_ascending'), category('gift-sets?sort=price_descending'), category('wax-melts?sort=newest'),
      at('/blog?page=2'), at('/?fb=1'), product('travel-tin-trio?ref=instagram'), product('gift-card?ref=newsletter'),
      product('beeswax-taper-pair-1')
    ],
    dupNoCanonical: [
      ...PRODUCTS.slice(6, 12).map(slug => product(`${slug}?fb=1`)), category('all-products?page=2'), category('seasonal?sort=newest'),
      product('beeswax-taper-pair-1'), product('amber-jar-candle-8-oz-1'), product('copy-of-wooden-wick-tumbler'), product('gift-card-1'),
      `${HTTP}/product-page/travel-tin-trio`, `${HTTP}/category/gift-sets`,
      `${BARE}/post/soy-vs-beeswax`, `${BARE}/product-page/brass-wick-trimmer`
    ],
    dupGoogleChose: [
      product('beeswax-taper-pair-1'), product('amber-jar-candle-8-oz-1'), product('honey-glow-tealights-pack-of-12'), product('honey-glow-tealights-pack-of-24'), product('linen-dust-cover-large'),
      category('gifts'), category('gift-ideas'), post('candle-care-guide-2')
    ],
    redirect: [
      `${HTTP}/`, `${HTTP}/product-page/cedar-and-sage-soy-candle`, `${HTTP}/product-page/beeswax-taper-pair`, `${HTTP}/category/gift-sets`, `${HTTP}/post/soy-vs-beeswax`,
      `${HTTP_BARE}/`, `${HTTP_BARE}/product-page/amber-jar-candle-8-oz`, `${HTTP_BARE}/candle-care`, `${HTTP_BARE}/category/wax-melts`, `${HTTP_BARE}/product-page/gift-card`,
      `${BARE}/`, `${BARE}/product-page/travel-tin-trio`, `${BARE}/product-page/black-fig-reed-diffuser`, `${BARE}/category/seasonal`, `${BARE}/post/why-candles-tunnel`, `${BARE}/faq`, `${BARE}/wholesale`, `${BARE}/product-page/smoked-vanilla-pillar`, `${BARE}/category/accessories`,
      ...RENAMED.map(product),
      post('soy-vs-beeswax-wax'), post('candle-care-tips'),
      category('gifts'),
      product('cedar-sage-candle?fb=1'), product('fig-diffuser?ref=instagram'), at('/shop?page=2')
    ],
    notFound: [
      ...OLD_PRODUCTS.slice(0, 14).map(product),
      category('halloween'), category('summer-2025'),
      post('spring-sale-2025'), post('black-friday-gift-guide-2025'), post('new-scent-announcement'),
      at('/black-friday-2025'), at('/old-shipping-policy'),
      product('holiday-advent-candle-set?fb=1')
    ],
    noindex: [
      at('/blog/tags/gifts'), at('/blog/tags/soy-wax'), at('/blog/tags/candle-care'), at('/blog/tags/seasonal'), at('/blog/tags/how-to'), at('/blog/tags/behind-the-scenes'), at('/blog/tags/scents'), at('/blog/tags/diy'),
      at('/blog/archive/09/2026'), at('/blog/archive/08/2026'), at('/blog/archive/07/2026'), at('/blog/archive/06/2026'), at('/blog/archive/05/2026'), at('/blog/archive/04/2026'),
      at('/members-area/maple-reader/profile'), at('/members-area/wick-fan/profile'), at('/members-area/oak-and-ash/profile'), at('/members-area/candle-club/profile'),
      product('wholesale-sample-box'), product('staff-test-product'), product('gift-card'), product('unscented-pillar-candle'),
      at('/wholesale-application'), at('/thank-you'),
      at('/cart-page'), at('/checkout'), at('/thank-you-page'),
      category('wholesale-only'), post('pouring-day')
    ],
    soft404: [
      ...OLD_PRODUCTS.slice(4, 10).map(product),
      post('new-scent-announcement'), post('summer-pop-up-market'),
      category('halloween'), category('clearance')
    ],
    robots: [
      at('/cart-page'), at('/checkout'), at('/thank-you-page'),
      at('/members-area/maple-reader/profile'), at('/members-area/wick-fan/profile'),
      at('/_api/common-services/notification/invoke'),
      product('staff-test-product'), at('/wholesale-price-list')
    ],
    serverError: [product('travel-tin-trio'), product('candle-making-kit'), product('gift-card'), category('seasonal'), post('holiday-gift-guide')],
    redirectError: [product('cedar-sage-candle'), product('beeswax-tapers'), at('/old-shop'), `${HTTP_BARE}/product-page/amber-candle-8oz`],
    unauthorized: [at('/members-area/maple-reader/profile'), at('/members-area/wick-fan/profile'), at('/wholesale-price-list')],
    forbidden: [at('/members-area/maple-reader/profile'), at('/members-area/wick-fan/profile'), at('/members-area/candle-club/profile'), at('/members-area/oak-and-ash/profile'), product('wholesale-sample-box')],
    other4xx: [product('cedar-and-sage-soy-candle%'), product('travel-tin-trio%2'), at('/collections/all/candles')],
    removal: [product('staff-test-product'), product('limited-gingerbread-candle'), product('wholesale-sample-box')],
    dupSubmitted: [product('beeswax-taper-pair-1'), product('amber-jar-candle-8-oz-1'), product('copy-of-wooden-wick-tumbler'), product('gift-card-1'), product('honey-glow-tealights-pack-of-12'), product('linen-dust-cover-large'), category('all-products?page=2'), product('travel-tin-trio?fb=1')],
    crawlAnomaly: [at('/_api/common-services/notification/invoke'), product('travel-tin-trio'), product('candle-making-kit')],
    indexedBlocked: [at('/cart-page'), at('/checkout'), at('/_api/common-services/notification/invoke'), product('staff-test-product')],
    indexedNoContent: [product('gift-card'), product('soy-wax-flakes-refill'), at('/lookbook')]
  };

  const SAMPLES = Object.freeze(Object.fromEntries(REASONS.map((reason, index) => [reason.id, Object.freeze({
    store: STORE,
    site: SITE,
    reason: reason.id,
    text: shuffle(RECIPES[reason.id], 2026 + index * 7).join('\n')
  })])));
  // The worked example on the page and on the /tools card.
  const SAMPLE = SAMPLES.crawled;

  // Line breaks and spaces aside, is this text one of the samples?
  const normalized = text => String(text == null ? '' : text).replace(/\r\n?/g, '\n').split('\n').map(line => line.trim()).filter(Boolean).join('\n');
  const SAMPLE_TEXTS = new Map(Object.values(SAMPLES).map(sample => [normalized(sample.text), sample]));
  function sampleFor(text) {
    return SAMPLE_TEXTS.get(normalized(text)) || null;
  }

  // The /tools card: the sample's top three groups and the verdict line,
  // exactly as the tool prints them. share is the whole-number percent.
  function directoryPreview() {
    const result = decode(SAMPLE);
    return {
      reason: result.reason.label,
      total: result.total,
      groups: result.groups.slice(0, 3).map(group => ({ label: group.label, count: group.count, share: group.share })),
      verdict: { level: result.verdict.level, text: result.verdict.text }
    };
  }

  // ---- The status table, built from the verdict rules --------------------------

  const SHORT_NAMES = { params: '? copies', http: 'http:// URLs', hostVariant: 'www or non-www URLs', freeWix: 'your free Wix URL', blogTag: 'blog tags', blogArchive: 'blog archives', member: 'member pages', system: 'system pages', wixApi: 'Wix system URLs' };
  // A rule the verdicts don't back up never reaches the table.
  function wixSummary(reason) {
    const level = rule(reason, 'product').level;
    const normalFor = reason.normalFor.filter(type => rule(reason, type).level === 'normal').map(type => SHORT_NAMES[type]);
    return { level, normalFor };
  }
  function firstCheck(reason) {
    const id = stepIdFor(rule(reason, 'product').steps[0] || 'inspect', 'product');
    return fill(STEPS[id].text, { count: 2, one: 'product page', many: 'product pages', sitemap: TYPES.product.sitemap });
  }

  // ---- Markup shared by the server render and the browser ----------------------

  const ICONS = {
    normal: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3.5 8.5l3 3 6-7"/></svg>',
    look: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M1.5 8s2.4-4.5 6.5-4.5S14.5 8 14.5 8 12.1 12.5 8 12.5 1.5 8 1.5 8z"/><circle cx="8" cy="8" r="1.9"/></svg>',
    fix: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M8 2.2l6.3 11.3H1.7z"/><path d="M8 6.6v3.1"/><path d="M8 11.7v.1"/></svg>'
  };
  const chip = level => `<span class="wid-chip" data-level="${level}">${ICONS[level]}${LEVEL_WORD[level]}</span>`;
  const sourceLinks = keys => keys.filter(key => SOURCES[key]).map(key => `<a href="${SOURCES[key][1]}">${esc(SOURCES[key][0].replace(/^[^:]+: /, ''))}</a>`);

  function reasonOptionsMarkup(selectedId) {
    return REASON_GROUPS.map(([group, title]) => `<optgroup label="${esc(title)}">${REASONS.filter(reason => reason.group === group).map(reason => `<option value="${reason.id}"${reason.id === selectedId ? ' selected' : ''}>${esc(reason.label)}</option>`).join('')}</optgroup>`).join('');
  }

  function meaningMarkup(reason) {
    const also = reason.shownAs ? ` <span class="wid-also">Search Console may show it as “${esc(reason.shownAs)}”.</span>` : '';
    return `${esc(reason.meaning)}${also}`;
  }

  function countMarkup(result) {
    return result.total ? plural(result.total, 'URL') : '';
  }

  // The line under the paste box: what was read, skipped, repeated or cut.
  function parseNoteMarkup(result, sample) {
    const parse = result.parse;
    if (sample) return `<span class="wid-sample-tag">Made-up sample</span> ${esc(sample.store)} isn’t a real store. Paste your own list to replace it.`;
    const notes = [];
    if (parse.header) notes.push(`Read the “${esc(parse.header)}” column`);
    if (parse.repeats) notes.push(`${plural(parse.repeats, 'repeat')} counted once`);
    if (parse.dates) notes.push(`${plural(parse.dates, 'date')} ignored`);
    if (parse.skipped) notes.push(`${plural(parse.skipped, 'line')} skipped, not web addresses: ${parse.skippedSamples.map(line => `“${esc(line)}”`).join(', ')}${parse.skipped > parse.skippedSamples.length ? ' …' : ''}`);
    if (parse.truncated) {
      notes.push(parse.cut
        ? `only the first ${num(MAX_URLS)} were sorted; more weren’t (the text was too long to count them)`
        : `only the first ${num(MAX_URLS)} were sorted; ${num(parse.notSorted)} more weren’t`);
    } else if (parse.cut) notes.push('the text was too long, so the end was cut');
    if (!notes.length) return '';
    const text = notes.join('. ');
    return `${text.charAt(0).toUpperCase()}${text.slice(1)}.`;
  }

  function barMarkup(result) {
    const shown = result.groups.filter(group => group.slot);
    const rest = result.groups.filter(group => !group.slot);
    const segments = shown.map(group => ({ slot: group.slot, count: group.count, title: `${group.label}: ${group.count} (${group.shareText})` }));
    if (rest.length) {
      const count = rest.reduce((n, group) => n + group.count, 0);
      segments.push({ slot: 0, count, title: `Other types: ${count} (${shareOf(count, result.total).shareText})` });
    }
    // Numbers live in the legend beside the bar; the bar only shows shares.
    return segments.map(segment => `<span class="wid-seg wid-c${segment.slot}" style="flex-grow:${segment.count}" title="${esc(segment.title)}"></span>`).join('');
  }

  function legendMarkup(result) {
    const rows = result.groups.map(group => `<li><a class="wid-legend-row" href="#wid-group-${group.type}"><span class="wid-swatch wid-c${group.slot}" aria-hidden="true"></span><span class="wid-legend-label">${esc(group.label)}</span><span class="wid-legend-count"><b>${group.count.toLocaleString('en-US')}</b><span> · ${group.shareText}</span></span></a>${chip(group.level)}</li>`);
    return rows.join('');
  }

  // A guessed main address comes with both forms of it, www and not, as buttons.
  function hostChoiceMarkup(result) {
    const site = result.site;
    if (!site.inferred || site.choices.length !== 2) return '';
    const [main, other] = site.choices;
    const why = {
      both: `Your list has URLs on both. Sorted for now as if it’s <b>${esc(main)}</b>, the form most https:// URLs here use.`,
      only: `Your list only has URLs on <b>${esc(main)}</b>, so it’s sorted for now as if that’s your main address.`,
      redirect: `Wix sends the address without www to www, so this list is sorted for now as if <b>${esc(main)}</b> is your main address.`
    }[site.why] || '';
    return `<div class="wid-choice"><p><strong>Is ${esc(main)} your main address, or ${esc(other)}?</strong> ${why}</p><div class="wid-choice-buttons">${site.choices.map(host => `<button type="button" class="wid-pick" data-wid-pick-host="${esc(host)}">${esc(host)}</button>`).join('')}</div></div>`;
  }

  // Typed a site address, but none of the URLs are on it: offer to clear it.
  function siteMismatchMarkup(result) {
    if (!result.site.noneOnSite) return '';
    return `<div class="wid-choice" data-level="fix"><p><strong>None of these URLs are on ${esc(result.site.mainHost)}</strong>, the site address you typed. Check it, or clear it and the decoder will use the most common address in the list.</p><div class="wid-choice-buttons"><button type="button" class="wid-pick wid-pick-plain" data-wid-clear-site>Clear the site address</button></div></div>`;
  }

  // "For: …" names the groups a step is for, unless it's for all of them.
  function stepsListMarkup(steps, groupCount = 0) {
    return steps.map(step => `<li><p>${step.text}${step.sources.length ? ` <span class="wid-cite">(${sourceLinks(step.sources).join(', ')})</span>` : ''}</p>${groupCount > 1 && step.groups && step.groups.length && step.groups.length < groupCount ? `<p class="wid-for">For: ${esc(joinWords(step.groups))}</p>` : ''}</li>`).join('');
  }

  function emptyMarkup() {
    return `<div class="wid-empty"><p class="wid-empty-title">Paste the URLs from Search Console to sort them here.</p><ol class="wid-empty-steps"><li>In Search Console, open <b>Indexing</b> › <b>Pages</b> (menu names can change).</li><li>Under <b>Why pages aren’t indexed</b>, click a reason.</li><li>Copy the example URLs, or export them, and paste them into the URL box.</li></ol><p class="wid-empty-note">Or press <b>Try a sample</b> to see a made-up store’s list.</p></div>`;
  }

  // The summary sentence with its numbers kept on one line ("37 (62%)").
  function sentenceMarkup(result) {
    const top = result.groups[0];
    const lead = `You pasted <b class="wid-nowrap">${esc(plural(result.total, 'URL'))}</b> under “${esc(result.reason.label)}”.`;
    if (result.groups.length === 1) return `${lead} ${result.total === 1 ? `It’s ${esc(top.a)}.` : `${allOf(result.total)} are ${esc(top.many)}.`}`;
    return `${lead} <b class="wid-nowrap">${num(top.count)} (${top.shareText})</b> ${top.count === 1 ? `is ${esc(top.a)}` : `are ${esc(top.many)}`}.`;
  }

  // The answer column: sentence, bar, verdict, what it means, what to do.
  function resultMarkup(result) {
    if (!result.total) {
      const nothing = result.parse.skipped || result.parse.dates
        ? `<div class="wid-empty"><p class="wid-empty-title">No web addresses found.</p><p>Paste URLs that start with https://, or paths like /product-page/…, one per line.</p></div>`
        : emptyMarkup();
      return nothing;
    }
    const groupsWord = plural(result.groups.length, 'page type');
    // Redirects are normal, but each should land on the right page.
    const idle = result.reason.id === 'redirect'
      ? `Nothing to fix. If you like, open ${result.total === 1 ? 'it to check it lands' : 'a few to check they land'} on the right page.`
      : `Nothing to fix for this list. Leave ${result.total === 1 ? 'this page as it is' : 'these pages as they are'}.`;
    return `<p class="wid-sentence">${sentenceMarkup(result)}</p>
          <div class="wid-chart"><div class="wid-bar" aria-hidden="true">${barMarkup(result)}</div><p class="wid-visually-hidden">${esc(`${plural(result.total, 'URL')} in ${groupsWord}:`)}</p><ul class="wid-legend" aria-label="${esc(`Page types, ${groupsWord}`)}">${legendMarkup(result)}</ul></div>
          <div class="wid-verdict" data-level="${result.verdict.level}">${chip(result.verdict.level)}<p>${esc(result.verdict.text)}</p></div>
          ${hostChoiceMarkup(result)}${siteMismatchMarkup(result)}
          <div class="wid-means"><h3>What this usually means on Wix</h3>${result.means.map(paragraph => `<p>${esc(paragraph)}</p>`).join('')}<p class="wid-cite">${sourceLinks(result.reason.sources).join(' · ')}</p></div>
          ${result.firstSteps.length ? `<div class="wid-todo"><h3>What to do first</h3><ol>${stepsListMarkup(result.firstSteps, result.groups.filter(group => group.level !== 'normal').length)}</ol></div>` : `<div class="wid-todo"><h3>What to do</h3><p class="wid-nothing">${idle}</p></div>`}
          ${result.dont.length ? `<div class="wid-dont"><h3>Don’t</h3><ul>${result.dont.map(item => `<li>${esc(item)}</li>`).join('')}</ul></div>` : ''}`;
  }

  function cardMarkup(group) {
    const steps = group.steps.length
      ? `<details class="wid-steps"><summary><span>What to do <span class="wid-steps-count">${plural(group.steps.length, 'step')}</span></span><span class="wid-disclosure-icon" aria-hidden="true"></span></summary><ol>${stepsListMarkup(group.steps)}</ol></details>`
      : '';
    const examples = group.examples.map(example => `<li><code class="wid-url">${esc(example.display)}</code><button type="button" class="wid-copy-url" data-wid-copy-url="${esc(example.copy)}" aria-label="${esc(`Copy URL ${example.display}`)}">Copy URL</button>${example.note ? `<span class="wid-url-note">${esc(example.note)}</span>` : ''}</li>`).join('');
    const title = group.level === 'normal' ? 'Examples' : 'Look at these first';
    return `<article class="wid-card" id="wid-group-${group.type}" data-level="${group.level}" aria-labelledby="wid-group-${group.type}-title">
            <div class="wid-card-head"><span class="wid-swatch wid-c${group.slot}" aria-hidden="true"></span><h3 id="wid-group-${group.type}-title">${esc(group.label)}</h3><p class="wid-card-count"><b>${group.count.toLocaleString('en-US')}</b> · ${group.shareText}</p></div>
            <p class="wid-card-hint">${esc(group.hint)}</p>
            <div class="wid-card-verdict">${chip(group.level)}</div>
            <p class="wid-card-text">${group.text}</p>
            ${steps}
            <div class="wid-examples"><h4>${title}</h4><ul>${examples}</ul>${group.level === 'normal' ? '' : '<p class="wid-examples-hint">Paste one into Search Console’s URL Inspection to see Google’s own explanation.</p>'}${group.count > 3 ? `<p class="wid-more-note"${group.count > group.examples.length ? '' : ' data-phone-only'}>All ${group.count.toLocaleString('en-US')} are in “Show all URLs” below.</p>` : ''}</div>
          </article>`;
  }

  function cardsMarkup(result) {
    return result.groups.map(cardMarkup).join('');
  }

  // "Show all": filter options and one page of rows, so 5,000 URLs never
  // become 5,000 table rows at once.
  function filterOptionsMarkup(result, selected) {
    const all = `<option value="all"${selected === 'all' ? ' selected' : ''}>All page types (${result.total.toLocaleString('en-US')})</option>`;
    return all + result.groups.map(group => `<option value="${group.type}"${selected === group.type ? ' selected' : ''}>${esc(group.label)} (${group.count.toLocaleString('en-US')})</option>`).join('');
  }

  function rowsFor(result, filter) {
    const rows = [];
    result.groups.forEach(group => {
      if (filter !== 'all' && group.type !== filter) return;
      group.urls.forEach(u => rows.push({ u, group }));
    });
    return rows;
  }

  // Paths for your main address, full URLs for everything else.
  function displayOf(u, site) {
    const onMain = u.relative || (site && u.host === site.mainHost && u.protocol === 'https');
    return onMain ? `${u.path}${u.query ? `?${u.query}` : ''}` : keyOf(u);
  }

  function rowsMarkup(rows, site) {
    return rows.map(({ u, group }) => `<tr role="row"><td role="cell"><code class="wid-url">${esc(displayOf(u, site))}</code></td><td role="cell"><span class="wid-swatch wid-c${group.slot}" aria-hidden="true"></span>${esc(group.label)}</td></tr>`).join('');
  }

  function shownMarkup(count, total) {
    return count >= total ? `Showing all ${total.toLocaleString('en-US')}.` : `Showing ${count.toLocaleString('en-US')} of ${total.toLocaleString('en-US')}.`;
  }

  // One sentence for screen readers after each change.
  function announcement(result) {
    if (!result.total) return result.parse.skipped ? 'No web addresses found in the list.' : 'The list is empty.';
    return `${result.sentence} ${result.verdict.text}`;
  }

  // The status table under the guide: every reason, built from REASONS and
  // the same verdict rules the decoder uses, so the two can't drift apart.
  function statusTableMarkup() {
    return REASONS.map(reason => {
      const summary = wixSummary(reason);
      const normal = summary.normalFor.length ? `${summary.level === 'normal' ? 'Also' : 'Normal'} for ${esc(joinWords(summary.normalFor))}.` : '';
      return `<tr role="row"><th scope="row" role="rowheader" data-label="Reason">${esc(reason.label)}${reason.group === 'older' ? ' <small>Older name</small>' : reason.group === 'indexed' ? ' <small>Indexed, with a warning</small>' : ''}</th><td role="cell" data-label="What it means">${esc(reason.meaning)}</td><td role="cell" data-label="On a Wix store">${chip(summary.level)} <span>for product pages.</span>${normal ? ` <span>${normal}</span>` : ''}</td><td role="cell" data-label="First thing to check">${firstCheck(reason)}</td></tr>`;
    }).join('');
  }

  function sourcesListMarkup() {
    return Object.keys(SOURCES).map(key => `<li><a href="${SOURCES[key][1]}">${esc(SOURCES[key][0])}</a></li>`).join('');
  }

  // ---- Page behaviour ----------------------------------------------------------

  function init(root) {
    if (!root || root.dataset.widReady === 'true') return;
    const find = name => root.querySelector(`[data-wid-${name}]`);
    const reasonSelect = find('reason');
    const textarea = find('urls');
    const siteInput = find('site');
    const resultBox = find('result');
    const cards = find('cards');
    if (!reasonSelect || !textarea || !siteInput || !resultBox || !cards) return;
    root.dataset.widReady = 'true';
    const productPrefix = find('prefix-product');
    const postPrefix = find('prefix-post');
    const meaning = find('meaning');
    const count = find('count');
    const note = find('note');
    const fileInput = find('file');
    const fileError = find('file-error');
    const siteError = find('site-error');
    const groupsSection = find('groups');
    const allDetails = find('all');
    const allCount = find('all-count');
    const filterSelect = find('filter');
    const rowsBody = find('rows');
    const shownNote = find('shown');
    const moreButton = find('more');
    const copyButton = find('copy');
    const downloadButton = find('download');
    const includeBox = find('include');
    const status = find('status');
    const fallback = find('fallback');
    const fallbackText = find('fallback-text');
    const announcer = find('announcement');
    const sampleFlag = find('sample-flag');

    let result = null;
    let parsed = { text: null, value: null };
    let reported = false;
    let renderTimer = 0;
    let announceTimer = 0;
    let filter = 'all';
    let limit = PAGE_SIZE;
    const copiedTimers = new Map();

    const toolEvent = name => document.dispatchEvent(new CustomEvent('myuser:tool-event', { detail: { name } }));
    const setStatus = message => { if (status) status.textContent = message; };
    // Rewriting unchanged markup would drop focus and replace a button
    // between its mousedown and its click.
    const shown = new Map();
    function setHtml(element, html) {
      if (!element || shown.get(element) === html) return;
      shown.set(element, html);
      element.innerHTML = html;
    }

    function readInput() {
      const text = textarea.value;
      const sample = sampleFor(text);
      if (parsed.text !== text) parsed = { text, value: parseUrls(text) };
      return {
        sample,
        input: {
          reason: reasonSelect.value,
          parsed: parsed.value,
          // A sample always sorts against its own made-up address; the site
          // address you typed is kept for your own list.
          site: sample ? sample.site : siteInput.value.trim(),
          prefixes: { product: productPrefix ? productPrefix.value : '', post: postPrefix ? postPrefix.value : '' }
        }
      };
    }

    function renderAll() {
      if (!allDetails) return;
      allDetails.hidden = !result.total;
      if (allCount) allCount.textContent = result.total.toLocaleString('en-US');
      if (!allDetails.open || !result.total) return;
      if (filter !== 'all' && !result.groups.some(group => group.type === filter)) filter = 'all';
      setHtml(filterSelect, filterOptionsMarkup(result, filter));
      if (filterSelect) filterSelect.value = filter;
      const rows = rowsFor(result, filter);
      setHtml(rowsBody, rowsMarkup(rows.slice(0, limit), result.site));
      if (shownNote) shownNote.textContent = shownMarkup(Math.min(limit, rows.length), rows.length);
      if (moreButton) moreButton.hidden = rows.length <= limit;
    }

    function render(announce) {
      const { sample, input } = readInput();
      result = decode(input);
      root.dataset.widState = result.total ? 'ready' : 'empty';
      setHtml(meaning, meaningMarkup(result.reason));
      if (count) count.textContent = countMarkup(result);
      setHtml(note, parseNoteMarkup(result, sample));
      if (note) note.hidden = !note.innerHTML;
      if (sampleFlag) sampleFlag.hidden = !(sample && result.total);
      if (siteError) {
        const message = result.site.error
          ? 'That doesn’t look like a site address. Type it like www.yourstore.com.'
          : result.site.noneOnSite ? `None of these URLs are on ${esc(result.site.mainHost)}. Check the site address.` : '';
        setHtml(siteError, message);
        siteError.hidden = !message;
        if (message) siteInput.setAttribute('aria-invalid', 'true');
        else siteInput.removeAttribute('aria-invalid');
      }
      setHtml(resultBox, resultMarkup(result));
      setHtml(cards, cardsMarkup(result));
      if (groupsSection) groupsSection.hidden = !result.total;
      renderAll();
      if (copyButton) copyButton.disabled = !result.total;
      if (downloadButton) downloadButton.disabled = !result.total;
      if (!reported && result.total && !sample) {
        reported = true;
        toolEvent('wix_indexing_decoded');
      }
      if (announce && announcer) {
        clearTimeout(announceTimer);
        announceTimer = setTimeout(() => { announcer.textContent = announcement(result); }, 800);
      }
    }

    // Typing in a long list waits a little longer before sorting again.
    function schedule() {
      clearTimeout(renderTimer);
      hideFallback();
      setStatus('');
      renderTimer = setTimeout(() => render(true), textarea.value.length > 200000 ? 450 : 180);
    }

    function hideFallback() {
      if (fallback) fallback.hidden = true;
    }

    async function copy(value, message, eventName) {
      hideFallback();
      try {
        if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(value);
        setStatus(message);
        if (eventName) toolEvent(eventName);
        return true;
      } catch (_) {
        // Some browsers block the Clipboard API. Select the text in a box
        // and try the older copy command before asking for ⌘C / Ctrl+C.
        if (!fallback || !fallbackText) { setStatus('Couldn’t copy automatically.'); return false; }
        fallbackText.value = value;
        fallback.hidden = false;
        fallbackText.focus();
        fallbackText.select();
        try { fallbackText.setSelectionRange(0, value.length); } catch (__) { /* not selectable */ }
        let copied = false;
        try { copied = typeof document.execCommand === 'function' && document.execCommand('copy'); } catch (__) { copied = false; }
        if (copied) {
          fallback.hidden = true;
          if (eventName) toolEvent(eventName);
        }
        setStatus(copied ? message : 'Couldn’t copy automatically. The text is selected in the box below, so copy it from there.');
        return copied;
      }
    }

    // A pasted sample is replaced in one go: the first focus selects it.
    let sampleArmed = true;
    textarea.addEventListener('focus', () => {
      if (!sampleArmed) return;
      sampleArmed = false;
      if (sampleFor(textarea.value)) {
        textarea.select();
        try { textarea.setSelectionRange(0, textarea.value.length); } catch (_) { /* not selectable */ }
      }
    });
    textarea.addEventListener('input', schedule);
    siteInput.addEventListener('input', schedule);
    [productPrefix, postPrefix].forEach(input => { if (input) input.addEventListener('input', schedule); });
    reasonSelect.addEventListener('change', () => {
      // Switching reason while a sample is showing swaps in that reason's sample.
      if (sampleFor(textarea.value)) textarea.value = SAMPLES[reasonSelect.value].text;
      hideFallback();
      setStatus('');
      render(true);
    });

    root.addEventListener('click', event => {
      const target = event.target.closest('button, a');
      if (!target || !root.contains(target)) return;
      if (target.matches('[data-wid-sample]')) {
        textarea.value = (SAMPLES[reasonSelect.value] || SAMPLE).text;
        sampleArmed = true;
        if (fileError) fileError.hidden = true;
        render(true);
        return;
      }
      if (target.matches('[data-wid-clear]')) {
        textarea.value = '';
        if (fileError) fileError.hidden = true;
        render(true);
        textarea.focus();
        return;
      }
      if (target.matches('[data-wid-clear-site]')) {
        siteInput.value = '';
        render(true);
        siteInput.focus();
        return;
      }
      if (target.matches('[data-wid-pick-host]')) {
        siteInput.value = target.dataset.widPickHost;
        render(true);
        siteInput.focus();
        return;
      }
      if (target.matches('[data-wid-copy-url]')) {
        const value = target.dataset.widCopyUrl;
        copy(value, '').then(done => {
          if (!done) return;
          target.textContent = 'Copied';
          if (announcer) announcer.textContent = 'URL copied.';
          clearTimeout(copiedTimers.get(target));
          copiedTimers.set(target, setTimeout(() => { target.textContent = 'Copy URL'; }, 1600));
        });
        return;
      }
      if (target.matches('[data-wid-more]')) {
        limit += PAGE_SIZE;
        renderAll();
        return;
      }
      if (target.matches('[data-wid-copy-list]')) {
        const urls = rowsFor(result, filter).map(row => fullUrl(row.u, result.site));
        copy(urls.join('\n'), `${plural(urls.length, 'URL')} copied.`);
      }
    });

    if (allDetails) allDetails.addEventListener('toggle', renderAll);
    // The table of every reason is open on wide screens. On a phone it is
    // twenty cards long, so it starts closed there (it sits far below the
    // first screen, so nothing visible moves).
    const reasonsTable = document.querySelector('[data-wid-reasons]');
    if (reasonsTable && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 650px)').matches) reasonsTable.open = false;
    if (filterSelect) filterSelect.addEventListener('change', () => { filter = filterSelect.value; limit = PAGE_SIZE; renderAll(); });

    if (copyButton) copyButton.addEventListener('click', () => {
      if (result && result.total) copy(describe(result, { includeUrls: Boolean(includeBox && includeBox.checked) }), 'Summary copied.', 'wix_summary_copied');
    });
    if (downloadButton) downloadButton.addEventListener('click', () => {
      if (!result || !result.total) return;
      const URLs = window.URL;
      if (!URLs || typeof URLs.createObjectURL !== 'function' || typeof window.Blob !== 'function') {
        setStatus('Download isn’t available in this browser. Use Copy summary instead.');
        return;
      }
      const url = URLs.createObjectURL(new window.Blob([describe(result, { includeUrls: Boolean(includeBox && includeBox.checked) })], { type: 'text/plain;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `wix-indexing-${result.reason.id}.txt`;
      link.hidden = true;
      root.append(link);
      link.click();
      link.remove();
      setTimeout(() => URLs.revokeObjectURL(url), 1000);
      setStatus('Summary downloaded.');
    });

    // A file is read here, in the browser. Nothing is uploaded.
    if (fileInput) fileInput.addEventListener('change', () => {
      const file = fileInput.files && fileInput.files[0];
      fileInput.value = '';
      if (!file) return;
      const fail = message => {
        if (fileError) { fileError.textContent = message; fileError.hidden = false; }
      };
      if (fileError) fileError.hidden = true;
      if (file.size > MAX_FILE_BYTES) { fail('That file is over 5 MB. Choose the CSV for one reason, or paste the URL column.'); return; }
      const reader = new FileReader();
      reader.onload = () => {
        const text = String(reader.result || '');
        if (/^PK\u0003\u0004/.test(text)) { fail('That’s a ZIP or Excel file. Open it, then choose the CSV with the URLs in it, or copy the URL column and paste it here.'); return; }
        if (/\u0000/.test(text.slice(0, 2000))) { fail('That file isn’t plain text. Save it as CSV UTF-8 and try again.'); return; }
        textarea.value = text;
        render(true);
      };
      reader.onerror = () => fail('Couldn’t read that file. Try again, or paste the URLs instead.');
      reader.readAsText(file);
    });

    window.addEventListener('pagehide', () => { clearTimeout(renderTimer); clearTimeout(announceTimer); });
    render(false);
  }

  return Object.freeze({
    META,
    REASONS,
    TYPES,
    SOURCES,
    SAMPLE,
    SAMPLES,
    MAX_URLS,
    findReason,
    labelKey,
    readAddress,
    parseUrls,
    siteContext,
    pageType,
    classify,
    prefixTable,
    decode,
    describe,
    directoryPreview,
    sampleFor,
    wixSummary,
    firstCheck,
    reasonOptionsMarkup,
    meaningMarkup,
    countMarkup,
    parseNoteMarkup,
    resultMarkup,
    cardsMarkup,
    filterOptionsMarkup,
    rowsFor,
    rowsMarkup,
    shownMarkup,
    statusTableMarkup,
    sourcesListMarkup,
    announcement,
    plainText,
    esc,
    PAGE_SIZE,
    init
  });
});
